import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Sparkles,
  RotateCcw,
  Send,
  Flame,
  ScrollText,
  BookOpen,
  Eye,
  EyeOff,
  Share2,
  Wallpaper,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { MARKDOWN_REMARK_PLUGINS } from "@/lib/markdown";
import { toast } from "sonner";

function YinYang({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" className={className} fill="currentColor">
      <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="4" />
      <path
        d="M 50 4 A 46 46 0 0 0 50 96 A 23 23 0 0 0 50 50 A 23 23 0 0 1 50 4 M 50 19.5 A 7.5 7.5 0 1 0 50 34.5 A 7.5 7.5 0 1 0 50 19.5 M 50 65.5 A 7.5 7.5 0 1 0 50 80.5 A 7.5 7.5 0 1 0 50 65.5 Z"
        fillRule="evenodd"
        fill="currentColor"
      />
    </svg>
  );
}
import { DivinerPicker } from "@/components/diviner-picker";
import { IchingSchoolPicker } from "@/components/iching-school-picker";
import { useRegisterTour } from "@/components/tour/tour-provider";
import { SiteHeader } from "@/components/site-header";
import { ShareDialog } from "@/components/share/share-dialog";
import { WallpaperDialog } from "@/components/wallpaper/wallpaper-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  createSession,
  castHexagram,
  sendChatMessage,
  rateChatMessage,
  getChatHistory,
  getSession,
  waitForChatJob,
  type ChatMessage,
  type IchingSchool,
  type HexagramResult,
  type LucHao,
} from "@/lib/boide-api";
import { refreshBoideUser, useBoideUser, getDefaultDivinerId } from "@/hooks/use-boide-user";
import { StarRating } from "@/components/star-rating";
import { AiWatermark } from "@/components/ai-watermark";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  type Line,
  lineToBit,
  queToLines,
  hexagramToLines,
  hexagramToBienLines,
  HexLine,
} from "@/components/iching/hexagram-full-board";
import { errorName, trackEvent } from "@/lib/analytics";
import { clampWords } from "@/lib/chat-limit";

export const Route = createFileRoute("/_authenticated/kinh-dich")({
  validateSearch: (search: Record<string, unknown>): { session?: string; question?: string } => ({
    session: typeof search.session === "string" ? search.session : undefined,
    question: typeof search.question === "string" ? search.question : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Gieo Quẻ Kinh Dịch Online Miễn Phí - Bói Quẻ Dịch 64 Quẻ" },
      {
        name: "description",
        content:
          "Gieo quẻ Kinh Dịch online miễn phí: tham vấn 64 quẻ Dịch, gieo ba đồng tiền cổ và luận giải bằng AI. Gieo quẻ ngay!",
      },
      { property: "og:title", content: "Gieo Quẻ Kinh Dịch Online Miễn Phí - Bói Quẻ Dịch" },
    ],
  }),
  component: IChingPage,
});

// ---------- Hexagram database (subset, will fall back to generic) ----------
type Hex = { name: string; vi: string; meaning: string; reading: string };
const HEXAGRAMS: Record<string, Hex> = {
  "111111": {
    name: "Càn",
    vi: "ĐẠI HỮU",
    meaning: "Mọi việc hanh thông",
    reading:
      "Trời cao thuần dương, sáng tạo dồi dào. Quân tử tự cường không nghỉ. **Thời cơ khởi sự lớn đã đến** — kiên trì chính đạo, tránh tự mãn.",
  },
  "000000": {
    name: "Khôn",
    vi: "THUẦN KHÔN",
    meaning: "Đại đức nhu thuận",
    reading:
      "Đất rộng nhu thuận, đức dày tải vạn vật. Hãy ôn hoà đi theo chính đạo, tiếp nhận và nuôi dưỡng. **Bao dung chính là sức mạnh.**",
  },
  "010001": {
    name: "Truân",
    vi: "THUỶ LÔI TRUÂN",
    meaning: "Khởi đầu gian nan",
    reading:
      "Mầm xuyên đất — gian nan thuở ban đầu. Đừng vội tiến, **tìm trợ lực, lập nền móng vững** trước khi bứt phá.",
  },
  "100010": {
    name: "Mông",
    vi: "SƠN THUỶ MÔNG",
    meaning: "Cần khai sáng",
    reading:
      "Tâm còn u mê, cần bậc minh sư khai mở. **Khiêm nhường thỉnh giáo**, chớ tự cho là đã biết.",
  },
  "010111": {
    name: "Nhu",
    vi: "THUỶ THIÊN NHU",
    meaning: "Chờ thời",
    reading:
      "Mây đã tụ trên trời — mưa sẽ đến. **Giữ tâm an, tích trữ tài lực** — vội vàng sẽ hỏng việc lớn.",
  },
  "111010": {
    name: "Tụng",
    vi: "THIÊN THUỶ TỤNG",
    meaning: "Tránh tranh chấp",
    reading:
      "Tranh tụng thị phi. Dù có lý cũng nên dừng lại, lui một bước. **Hoà giải tốt hơn thắng kiện.**",
  },
  "010000": {
    name: "Sư",
    vi: "ĐỊA THUỶ SƯ",
    meaning: "Đoàn kết quân tử",
    reading:
      "Việc lớn cần kỷ luật và tướng tài. **Chính danh — dụng nhân — đắc lòng người**, ắt thành công.",
  },
  "000010": {
    name: "Tỷ",
    vi: "THUỶ ĐỊA TỶ",
    meaning: "Thân ái tương trợ",
    reading:
      "Kết minh đúng người đúng lúc. Một mình không nên việc — **hãy đoàn kết, dựa vào quý nhân.**",
  },
};

function pickHex(key: string): Hex {
  return (
    HEXAGRAMS[key] ?? {
      name: "Vô danh",
      vi: "QUẺ ẨN",
      meaning: "Thiên cơ tiềm tàng",
      reading:
        "Quẻ này hiện ra trong khoảnh khắc tâm bạn còn dao động. **Hãy tịnh tâm, gieo lại** — hoặc đọc lời quẻ với tâm thế bao dung.",
    }
  );
}

// ---------- Coin / line generation ----------
const tossCoin = () => (Math.random() < 0.5 ? 2 : 3);
const tossLine = (): Line => {
  const v = tossCoin() + tossCoin() + tossCoin();
  return { value: v, changing: v === 6 || v === 9 };
};

type ScrollSection = {
  id: string;
  question: string;
  body: string;
  ready: boolean;
  messageId?: string; // id message AI thật (để chấm sao)
  rating?: number | null;
  ratingComment?: string | null;
  ratingCommentRewardedAt?: string | null;
  /** Lượt luận giải CHÍNH của quẻ (khác lượt thỉnh giáo thêm) — quyết định prompt gửi BE. */
  isPrimary?: boolean;
  /**
   * Lượt hỏi chưa có lời đáp và KHÔNG còn ai đang chạy — cho hiện nút thử lại.
   *
   * 🔴 Trường hợp chính là số dư không đủ: gieo quẻ miễn phí nên quẻ đã nằm trong DB,
   * còn lượt luận giải bị BE chặn trước khi trừ tiền. Không có nút này thì người dùng
   * nạp tiền xong vẫn không có đường nào luận giải lại đúng quẻ ấy.
   */
  failed?: boolean;
};

function messagesToSections(messages: ChatMessage[]): ScrollSection[] {
  const sections: ScrollSection[] = [];
  let pendingQuestion = "";

  messages.forEach((message, index) => {
    if (message.role === "user") {
      pendingQuestion = message.content;
      return;
    }

    if (message.role === "assistant") {
      sections.push({
        id: message.id || `history-${index}`,
        question: pendingQuestion,
        body: message.content,
        ready: true,
        messageId: message.id || undefined,
        rating: message.rating ?? null,
        ratingComment: message.rating_comment ?? null,
        ratingCommentRewardedAt: message.rating_comment_rewarded_at ?? null,
      });
      pendingQuestion = "";
    }
  });

  if (pendingQuestion) {
    sections.push({
      id: `history-user-${sections.length}`,
      question: pendingQuestion,
      body: "",
      ready: true,
    });
  }

  return sections;
}

// ---------- Phase machine ----------
/** Một Ô trong dải công cụ ở đầu bảng luận (mobile).
 *
 * `flex-1` là cả thiết kế: bốn ô BẰNG NHAU và dải trải kín chiều rộng, nên
 * trông cân đối thay vì bốn viên thuốc rộng chênh nhau nằm dồn một bên. Đo
 * thật: mỗi ô 72px ở máy 320px, 86px ở 375px — đủ chỗ cho cả bốn nhãn, không
 * cần bỏ chữ nút nào. */
const ICHING_TOOLBAR_SEG =
  "flex flex-1 items-center justify-center gap-1 py-1.5 text-[10px] text-primary transition-colors hover:bg-primary/5 active:bg-primary/10";

type Phase = "ask" | "casting" | "reading";

function IChingPage() {
  const { session, question: initialQuestion } = Route.useSearch();
  const { user } = useBoideUser();
  const [phase, setPhase] = useState<Phase>("ask");
  // Tour theo giai đoạn: màn đặt câu hỏi (ask) và màn đọc kết quả (reading) có bộ bước riêng.
  useRegisterTour(
    phase === "reading" ? "iching-result" : "iching",
    phase === "ask" || phase === "reading",
  );
  const [question, setQuestion] = useState(() => initialQuestion ?? "");
  const [focusAnswer, setFocusAnswer] = useState(false);
  const [isMetaCollapsed, setIsMetaCollapsed] = useState(true);
  const [divinerId, setDivinerId] = useState("cu_an");
  const [lines, setLines] = useState<Line[]>([]);

  // Set default diviner ID based on user profile when it loads
  useEffect(() => {
    if (!session && user?.profile) {
      setDivinerId(getDefaultDivinerId(user.profile));
    }
  }, [user, session]);
  const [drawnCount, setDrawnCount] = useState(0);
  const [shake, setShake] = useState(false);
  const [coinsFlying, setCoinsFlying] = useState(false);
  const [activeHao, setActiveHao] = useState<number | null>(null);
  const [charge, setCharge] = useState(0);
  const [bienRevealed, setBienRevealed] = useState(false);
  const [hexCracking, setHexCracking] = useState(false);
  const [scrollSections, setScrollSections] = useState<ScrollSection[]>([]);
  const [followUp, setFollowUp] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [backendHexagram, setBackendHexagram] = useState<HexagramResult | null>(null);
  // Giờ động tâm — mặc định là hiện tại, nhưng người dùng có thể an lại theo lúc tâm khởi.
  const [castAt, setCastAt] = useState<CastAt>(() => nowCastAt());
  const [castAtTouched, setCastAtTouched] = useState(false);
  // Trường phái luận — tuỳ chọn nâng cao, mặc định "tổng hợp" (Mai Hoa + Lục Hào: giữ đủ
  // dữ kiện thầy luận trước khi có tuỳ chọn này, user cũ không thấy khác). Nhớ lựa chọn trên
  // máy này cho lần gieo sau (chỉ là tiện ích; localStorage lỗi thì về mặc định).
  const [ichingSchool, setIchingSchoolState] = useState<IchingSchool>(() => {
    try {
      const v = typeof window !== "undefined" ? window.localStorage.getItem(ICHING_SCHOOL_KEY) : null;
      return v === "mai_hoa" || v === "luc_hao" ? v : "tong_hop";
    } catch {
      return "tong_hop";
    }
  });
  const setIchingSchool = (v: IchingSchool) => {
    setIchingSchoolState(v);
    try {
      window.localStorage.setItem(ICHING_SCHOOL_KEY, v);
    } catch {
      /* chế độ riêng tư / bị chặn lưu trữ — vẫn dùng được trong phiên này */
    }
  };
  const [shareOpen, setShareOpen] = useState(false);
  const [wallpaperOpen, setWallpaperOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const finalLinesRef = useRef<Line[] | null>(null);
  // Giữ message lỗi thật của lần tạo phiên/gieo quẻ để hiện đúng nguyên nhân
  // (vd "Nội dung quá dài, tối đa 2000 ký tự.") thay vì thông báo Token cứng.
  const beErrorRef = useRef<string | null>(null);
  const hexShareRef = useRef<HTMLDivElement>(null);

  // Listen to tour step changes and control mobile UI states and scrolling
  useEffect(() => {
    if (typeof window === "undefined") return;

    const runTourScroll = (tourId: string, stepIndex: number) => {
      const isVisible = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== "none" &&
          style.visibility !== "hidden"
        );
      };

      const pickVisibleElement = (sel: string): HTMLElement | null => {
        const elements = Array.from(document.querySelectorAll(sel));
        for (const el of elements) {
          if (el instanceof HTMLElement && isVisible(el)) {
            return el;
          }
        }
        const first = document.querySelector(sel);
        return first instanceof HTMLElement ? first : null;
      };

      {
        if (tourId === "iching-result") {
          if (stepIndex === 1 || stepIndex === 2) {
            // Step 2 & 3: Expand hexagram stage so lines are visible
            setIsMetaCollapsed(false);
            setFocusAnswer(false);
          } else if (stepIndex === 3) {
            // Step 4: Collapse hexagram stage and scroll to AI explanation
            setIsMetaCollapsed(true);
          }

          const resultStepSelectors = [
            "body", // Index 0
            '[data-tour="iching-quegoc"]', // Index 1
            '[data-tour="iching-haodong"]', // Index 2
            '[data-tour="iching-luangiai-header"]', // Index 3
            '[data-tour="iching-chat"]', // Index 4
          ];

          const selector = resultStepSelectors[stepIndex];
          if (selector && selector !== "body") {
            const doScroll = () => {
              const container = document.querySelector(
                '[data-tour="iching-result-scroll-container"]',
              );
              let target = pickVisibleElement(selector);
              if (stepIndex === 2 && (!target || !isVisible(target))) {
                target = pickVisibleElement('[data-tour="iching-quegoc"]');
              } else if (stepIndex === 3) {
                target =
                  pickVisibleElement('[data-tour="iching-luangiai-header"]') ||
                  pickVisibleElement('[data-tour="iching-luangiai-summary"]');
              }

              if (container instanceof HTMLElement && target instanceof HTMLElement) {
                const containerRect = container.getBoundingClientRect();
                const targetRect = target.getBoundingClientRect();
                const relativeTop = targetRect.top - containerRect.top + container.scrollTop;

                container.scrollTo({
                  top: Math.max(0, relativeTop - 16),
                  behavior: "auto",
                });

                // Dispatch resize event immediately so Joyride recalculates coordinates instantly
                window.dispatchEvent(new Event("resize"));

                // Keep a brief interval to handle any potential layout shifts or rendering delays
                const interval = setInterval(() => {
                  window.dispatchEvent(new Event("resize"));
                }, 50);
                setTimeout(() => {
                  clearInterval(interval);
                  window.dispatchEvent(new Event("resize"));
                }, 300);
              }
            };

            if (stepIndex === 3) {
              setTimeout(doScroll, 150); // index 3 needs extra time for collapse animation
            } else {
              doScroll();
            }
          }
        } else if (tourId === "iching") {
          // Gieo quẻ ritual stage steps (Step 2 to 5 / Index 1 to 4)
          const stepSelectors = [
            "body", // Index 0
            '[data-tour="iching-question"]', // Index 1
            '[data-tour="iching-casttime"]', // Index 2
            '[data-tour="iching-diviner"]', // Index 3
            '[data-tour="iching-cast"]', // Index 4
          ];
          const selector = stepSelectors[stepIndex];
          if (selector && selector !== "body") {
            const container = document.querySelector('[data-tour="iching-ask-scroll-container"]');
            const target = pickVisibleElement(selector);
            if (container instanceof HTMLElement && target instanceof HTMLElement) {
              const containerRect = container.getBoundingClientRect();
              const targetRect = target.getBoundingClientRect();
              const relativeTop = targetRect.top - containerRect.top + container.scrollTop;

              container.scrollTo({
                top: Math.max(0, relativeTop - 24),
                behavior: "auto",
              });

              // Dispatch resize event immediately so Joyride recalculates coordinates instantly
              window.dispatchEvent(new Event("resize"));

              // Keep a brief interval to handle any potential layout shifts or rendering delays
              const interval = setInterval(() => {
                window.dispatchEvent(new Event("resize"));
              }, 50);
              setTimeout(() => {
                clearInterval(interval);
                window.dispatchEvent(new Event("resize"));
              }, 300);
            }
          }
        }
      }
    };

    const handleTourEvent = (e: Event) => {
      const { tourId, stepIndex, type } = (e as CustomEvent).detail || {};
      if (type === "step:after" || type === "tour:start" || type === "step:before") {
        runTourScroll(tourId, stepIndex);
      }
    };

    const handlePrescroll = (e: Event) => {
      const { tourId, stepIndex } = (e as CustomEvent).detail || {};
      runTourScroll(tourId, stepIndex);
    };

    window.addEventListener("tour-event", handleTourEvent);
    window.addEventListener("tour-prescroll", handlePrescroll);
    return () => {
      window.removeEventListener("tour-event", handleTourEvent);
      window.removeEventListener("tour-prescroll", handlePrescroll);
    };
  }, []);

  const changeCastAt = (next: CastAt) => {
    setCastAtTouched(true);
    setCastAt(next);
  };

  const useCurrentCastAt = () => {
    setCastAtTouched(false);
    setCastAt(nowCastAt());
  };

  useEffect(() => {
    if (!session) return;
    let cancelled = false;

    (async () => {
      try {
        const [detail, history] = await Promise.all([getSession(session), getChatHistory(session)]);
        if (cancelled) return;

        const hydratedLines = hexagramToLines(detail.hexagram_result);
        setSessionId(detail.id);
        setDivinerId(detail.diviner_id || "cu_an");
        setQuestion(detail.subject || detail.hexagram_result?.viec_can_xem || "Phiên Kinh Dịch");
        setLines(hydratedLines);
        setBackendHexagram(detail.hexagram_result);
        setDrawnCount(hydratedLines.length);
        setPhase("reading");
        // Quẻ đã gieo nhưng CHƯA có lời luận giải (thường vì số dư không đủ lúc gieo):
        // dựng một section trống đánh dấu `failed` để nút "Luận giải quẻ" hiện ra. Không
        // tự gửi lượt luận giải ở đây — lượt ấy trừ tiền, phải do người dùng bấm.
        const history0 = messagesToSections(history.messages);
        const sections: ScrollSection[] =
          history0.length === 0 && detail.hexagram_result
            ? [
                {
                  id: "primary-pending",
                  question:
                    detail.subject || detail.hexagram_result.viec_can_xem || "Phiên Kinh Dịch",
                  body: "_Quẻ đã gieo nhưng chưa được luận giải._",
                  ready: true,
                  isPrimary: true,
                  failed: true,
                },
              ]
            : history0;
        revealSections(sections);
        const changingIndex = hydratedLines.findIndex((line) => line.changing);
        setActiveHao(changingIndex >= 0 ? changingIndex : null);
        setBienRevealed(hydratedLines.some((line) => line.changing));

        // Nối lại lượt chat đang chạy nền trên worker.
        //
        // 🔴 KHÔNG PHẢI tiện ích: BE chốt sổ (lưu câu trả lời + trừ tiền) ở nhịp hỏi
        // thăm, nên một lượt ĐÃ TRẢ TIỀN mà không còn ai hỏi thăm sẽ nằm treo cho tới
        // khi hết hạn. Tải lại trang giữa lượt chat là chuyện thường, và trước đây nó
        // đồng nghĩa với mất câu trả lời.
        //
        // Câu hỏi thì `messagesToSections` đã dựng sẵn thành một section body rỗng —
        // `ctr_chat` lưu tin nhắn của người dùng TRƯỚC khi giao việc, đúng để lúc này
        // có chỗ mà gắn câu trả lời vào.
        const waiting = sections[sections.length - 1];
        if (detail.pending_chat_job && waiting && !waiting.body) {
          const waitingId = waiting.id;
          setScrollSections((prev) =>
            prev.map((s) => (s.id === waitingId ? { ...s, ready: false } : s)),
          );
          void (async () => {
            try {
              const res = await waitForChatJob(detail.id, detail.pending_chat_job!);
              if (cancelled) return;
              if (res.message_id) {
                setScrollSections((prev) =>
                  prev.map((s) =>
                    s.id === waitingId ? { ...s, messageId: String(res.message_id) } : s,
                  ),
                );
              }
              streamInto(waitingId, res.reply || "_(Thầy chưa truyền lời nào)_");
            } catch (e) {
              if (cancelled) return;
              streamInto(
                waitingId,
                `**Thiên cơ bị che**: ${e instanceof Error ? e.message : String(e)}`,
              );
            } finally {
              if (!cancelled) void refreshBoideUser();
            }
          })();
        } else if (waiting && !waiting.body) {
          // Câu hỏi đã lưu nhưng lời đáp thì không, và cũng chẳng còn lượt nào chạy nền
          // (lượt cũ đã hết hạn / worker chết). Không đánh dấu `failed` là người xem
          // ngồi trước một con thái cực quay mãi không dừng.
          failSection(waiting.id, "_Lượt luận giải trước chưa hoàn tất._");
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Không tải được phiên Kinh Dịch");
      }
    })();

    return () => {
      cancelled = true;
    };
    // Chỉ chạy khi ĐỔI PHIÊN: `streamInto` được tạo mới mỗi lần render, đưa vào deps
    // là nạp lại cả phiên (và bắt đầu lại việc chờ lượt chat nền) sau mỗi lần gõ phím.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const hex = useMemo(() => {
    if (lines.length !== 6) return null;
    const key = lines
      .map((l) => lineToBit(l.value))
      .reverse()
      .join("");
    return pickHex(key);
  }, [lines]);

  // Quẻ Biến: ưu tiên hình quẻ từ BE, fallback tự flip khi BE chưa có.
  const bienLines = useMemo<Line[] | null>(() => {
    const backendBienLines = hexagramToBienLines(backendHexagram);
    if (backendBienLines.length === 6) return backendBienLines;
    if (lines.length !== 6) return null;
    if (!lines.some((l) => l.changing)) return null;
    return lines.map((l) =>
      l.changing
        ? { value: lineToBit(l.value) === "1" ? 6 : 9, changing: true }
        : { value: l.value, changing: false },
    );
  }, [backendHexagram, lines]);
  const bienHex = useMemo(() => {
    if (!bienLines) return null;
    const key = bienLines
      .map((l) => lineToBit(l.value))
      .reverse()
      .join("");
    return pickHex(key);
  }, [bienLines]);

  // ----- Audio -----
  const ensureAudio = () => {
    if (typeof window === "undefined") return null;
    if (!audioCtxRef.current) {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  };
  const playTing = (freq = 1800, dur = 0.45, vol = 0.18) => {
    const ctx = ensureAudio();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(freq, t0);
    osc.frequency.exponentialRampToValueAtTime(Math.max(80, freq * 0.55), t0 + dur);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(vol, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + dur);
  };

  // ----- BƯỚC 1: hold to charge -----
  const holdRef = useRef<number | null>(null);
  const chargeRef = useRef(0);
  useEffect(() => {
    chargeRef.current = charge;
  }, [charge]);
  const startCharge = () => {
    if (!question.trim() || phase !== "ask") return;
    ensureAudio();
    const start = performance.now();
    const DURATION = 1400;
    const tick = () => {
      const t = Math.min(1, (performance.now() - start) / DURATION);
      setCharge(t);
      if (t < 1) {
        holdRef.current = requestAnimationFrame(tick);
      } else {
        holdRef.current = null;
        cast();
      }
    };
    holdRef.current = requestAnimationFrame(tick);
  };
  const releaseCharge = () => {
    if (holdRef.current) {
      cancelAnimationFrame(holdRef.current);
      holdRef.current = null;
    }
    if (chargeRef.current >= 0.6) {
      // Smoothly fill remaining charge before casting
      const startVal = chargeRef.current;
      const duration = 200;
      const start = performance.now();
      const tick = () => {
        const elapsed = performance.now() - start;
        const progress = Math.min(1, elapsed / duration);
        const currentVal = startVal + (1 - startVal) * progress;
        setCharge(currentVal);
        if (progress < 1) {
          holdRef.current = requestAnimationFrame(tick);
        } else {
          holdRef.current = null;
          cast();
        }
      };
      holdRef.current = requestAnimationFrame(tick);
    } else {
      // Smoothly decay charge back to 0
      const startVal = chargeRef.current;
      const duration = 250;
      const start = performance.now();
      const tick = () => {
        const elapsed = performance.now() - start;
        const progress = Math.min(1, elapsed / duration);
        const currentVal = startVal * (1 - progress);
        setCharge(currentVal);
        if (progress < 1) {
          holdRef.current = requestAnimationFrame(tick);
        } else {
          holdRef.current = null;
        }
      };
      holdRef.current = requestAnimationFrame(tick);
    }
  };

  // ---------- BƯỚC 2: cast ritual ----------
  const cast = () => {
    if (!question.trim() || phase !== "ask") return;
    setCharge(1);
    setPhase("casting");
    setShake(true);
    trackEvent("iching_cast_start", {
      diviner_id: divinerId,
      question_length: question.trim().length,
    });
    setTimeout(() => setShake(false), 1600);

    finalLinesRef.current = null;
    beErrorRef.current = null;

    // Fire BE in parallel with the visual ritual; the chat needs the session_id
    const bePromise = (async () => {
      trackEvent("session_create_start", { diviner_id: divinerId, fortune_type: "iching" });
      const s = await createSession({
        subject: question,
        diviner_id: divinerId,
        fortune_type: "iching",
        iching_school: ichingSchool,
      });
      trackEvent("session_create_success", {
        diviner_id: divinerId,
        fortune_type: "iching",
        iching_school: ichingSchool,
      });
      setSessionId(s.session_id);
      try {
        const castMoment = castAtTouched ? castAt : nowCastAt();
        setCastAt(castMoment);
        const castResult = await castHexagram(s.session_id, castAtToIso(castMoment));
        const backendLines = hexagramToLines(castResult.hexagram_result);
        trackEvent("iching_cast_success", {
          has_changing_line: Boolean(castResult.hexagram_result.hao_dong),
        });
        setBackendHexagram(castResult.hexagram_result);
        finalLinesRef.current = backendLines;
      } catch (e) {
        trackEvent("iching_cast_error", { error_name: errorName(e) });
        console.warn("BE cast failed:", e);
      }
      return s.session_id;
    })().catch((e: Error) => {
      trackEvent("session_create_error", {
        diviner_id: divinerId,
        error_name: errorName(e),
        fortune_type: "iching",
      });
      // e.message đã chứa detail tiếng Việt từ BE (vd lỗi vượt ký tự) — hiện
      // đúng nguyên nhân, chỉ khi thật sự không có response mới coi là mất kết nối.
      const reason = e.message?.trim() || "Không kết nối được máy chủ.";
      beErrorRef.current = reason;
      toast.error(reason);
      return null;
    });

    setTimeout(() => {
      setCoinsFlying(true);
      playTing(2200, 0.35, 0.15);
    }, 700);

    const newLines: Line[] = Array.from({ length: 6 }, () => tossLine());
    setLines(newLines);
    setDrawnCount(0);
    let i = 0;
    const drawTick = () => {
      i++;

      // Sync with backend lines if they are available
      if (finalLinesRef.current && finalLinesRef.current.length === 6) {
        setLines((prev) => {
          const next = [...prev];
          for (let j = i - 1; j < 6; j++) {
            next[j] = finalLinesRef.current![j];
          }
          return next;
        });
      }

      setDrawnCount(i);

      setLines((currentLines) => {
        const line = currentLines[i - 1];
        const isYang = lineToBit(line.value) === "1";
        playTing(isYang ? 1700 - i * 60 : 1300 - i * 50, 0.32, 0.12);
        return currentLines;
      });

      if (i >= 6) {
        setTimeout(() => {
          setCoinsFlying(false);

          let currentLines = newLines;
          if (finalLinesRef.current && finalLinesRef.current.length === 6) {
            currentLines = finalLinesRef.current;
            setLines(currentLines);
          }

          const changingIdx = currentLines.findIndex((l) => l.changing);
          setActiveHao(changingIdx >= 0 ? changingIdx : 2);
          setPhase("reading");

          if (currentLines.some((l) => l.changing)) {
            setTimeout(() => {
              setHexCracking(true);
              playTing(800, 0.7, 0.2);
              setTimeout(() => {
                setBienRevealed(true);
                playTing(1500, 0.5, 0.16);
                // Stop the vibrating/cracking effect 1 second after the transformed hexagram begins fading in
                setTimeout(() => {
                  setHexCracking(false);
                }, 1000);
              }, 1200); // Let the lines vibrate/spark for 1.2s before revealing the next stage
            }, 300);
          }

          beginSection(question, true, currentLines, bePromise);
        }, 600);
        return;
      }
      setTimeout(drawTick, 280);
    };
    setTimeout(drawTick, 1700);
  };

  /** Gửi một lượt hỏi cho section đã dựng sẵn. Dùng cho cả lượt đầu và lượt thử lại. */
  const runSection = (
    id: string,
    q: string,
    isPrimary: boolean,
    sidPromise?: Promise<string | null>,
  ) => {
    // Lượt chạy lại phải xoá sạch dấu vết lượt trước (lỗi + cờ failed), nếu không
    // nút "Luận giải lại" vẫn nằm đó cạnh lời đáp mới.
    setScrollSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, body: "", ready: false, failed: false } : s)),
    );

    (async () => {
      const sid = sidPromise ? await sidPromise : sessionId;
      if (!sid) {
        // Hiện đúng nguyên nhân đã bắt được lúc tạo phiên (vd vượt số ký tự)
        // thay vì thông báo Token cứng gây hiểu nhầm.
        const reason = beErrorRef.current?.trim();
        failSection(
          id,
          reason ? `_${reason}_` : `_Không kết nối được Bói Đê. Vui lòng thử lại sau ít phút._`,
        );
        return;
      }
      const prompt = isPrimary
        ? `Hãy luận giải quẻ vừa gieo cho câu hỏi: "${q}". Trình bày: lời quẻ chính, tượng quẻ, hào động (nếu có), và lời khuyên cụ thể.`
        : q;
      try {
        trackEvent("iching_chat_send_start", {
          is_primary: isPrimary,
          message_length: prompt.length,
        });
        const res = await sendChatMessage({
          sessionId: sid,
          message: prompt,
          fortuneType: "iching",
        });
        trackEvent("iching_chat_send_success", {
          is_primary: isPrimary,
          message_length: prompt.length,
          reply_length: (res.reply || "").length,
        });
        if (res.message_id) {
          setScrollSections((prev) =>
            prev.map((s) => (s.id === id ? { ...s, messageId: String(res.message_id) } : s)),
          );
        }
        streamInto(id, res.reply || "_(Thầy chưa truyền lời nào)_");
      } catch (e) {
        trackEvent("iching_chat_send_error", {
          error_name: errorName(e),
          is_primary: isPrimary,
          message_length: prompt.length,
        });
        const msg = e instanceof Error ? e.message : String(e);
        failSection(id, `**Thiên cơ bị che**: ${msg}`);
      } finally {
        void refreshBoideUser();
      }
    })();
  };

  const beginSection = (
    q: string,
    isPrimary: boolean,
    _ls: Line[],
    sidPromise?: Promise<string | null>,
  ) => {
    const id = `s-${Date.now()}-${Math.random()}`;
    setScrollSections((prev) => [...prev, { id, question: q, body: "", ready: false, isPrimary }]);
    runSection(id, q, isPrimary, sidPromise);
  };

  /** Lượt hỏi thất bại: hiện nguyên nhân + mở nút thử lại (xem `ScrollSection.failed`). */
  const failSection = (id: string, message: string) => {
    setScrollSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, body: message, ready: true, failed: true } : s)),
    );
    scrollToBottom();
  };

  const retrySection = (section: ScrollSection) => {
    // Đang chạy thì không gửi lại. (Gửi lại ĐÚNG câu của một lượt còn dở trên worker
    // thì BE tự nối vào lượt ấy thay vì trừ tiền lần hai — xem `ctr_chat` bên ai-be.)
    if (!section.question.trim() || !section.ready) return;
    trackEvent("iching_chat_retry", { is_primary: Boolean(section.isPrimary) });
    runSection(section.id, section.question, Boolean(section.isPrimary));
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      const el = scrollRef.current;
      if (!el) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= 80) {
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  };

  const streamInto = (id: string, fullBody: string, delay = 600) => {
    const tokens = fullBody.match(/\S+\s*|\s+/g) ?? [fullBody];
    let i = 0;
    const tick = () => {
      if (i >= tokens.length) {
        setScrollSections((prev) => prev.map((s) => (s.id === id ? { ...s, ready: true } : s)));
        return;
      }
      const next = tokens[i++];
      setScrollSections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, body: s.body + next } : s)),
      );
      scrollToBottom();
      setTimeout(tick, 18 + Math.random() * 28);
    };
    setTimeout(tick, delay);
  };

  const revealSections = (sections: ScrollSection[]) => {
    // Hiển thị lịch sử ngay lập tức, không có typewriter animation
    setScrollSections(sections.map((section) => ({ ...section, ready: true })));
  };

  // Chấm sao câu trả lời (optimistic + revert nếu lỗi).
  const [ratingBusyId, setRatingBusyId] = useState<string | null>(null);
  const rateSection = async (
    messageId: string,
    value: number | null,
    ratingComment?: string | null,
  ) => {
    if (!sessionId) return;
    const prev = scrollSections.find((s) => s.messageId === messageId)?.rating ?? null;
    const prevComment =
      scrollSections.find((s) => s.messageId === messageId)?.ratingComment ?? null;
    setScrollSections((ss) =>
      ss.map((s) =>
        s.messageId === messageId
          ? {
              ...s,
              rating: value,
              ratingComment: ratingComment === undefined ? s.ratingComment : ratingComment,
            }
          : s,
      ),
    );
    setRatingBusyId(messageId);
    try {
      const result = await rateChatMessage(sessionId, messageId, value, ratingComment);
      if (result.rating_comment_rewarded_at) {
        setScrollSections((ss) =>
          ss.map((s) =>
            s.messageId === messageId
              ? { ...s, ratingCommentRewardedAt: result.rating_comment_rewarded_at }
              : s,
          ),
        );
      }
      if (result.rewarded) {
        toast.success("Đã hoàn 1.000đ vào ví cho nhận xét của bạn");
        void refreshBoideUser();
      }
      trackEvent("chat_message_rated", { fortune_type: "iching", rating: value ?? 0 });
    } catch (e) {
      setScrollSections((ss) =>
        ss.map((s) =>
          s.messageId === messageId ? { ...s, rating: prev, ratingComment: prevComment } : s,
        ),
      );
      toast.error(e instanceof Error ? e.message : "Không lưu được đánh giá");
    } finally {
      setRatingBusyId(null);
    }
  };

  const sendFollowUp = (e: React.FormEvent) => {
    e.preventDefault();
    const q = followUp.trim();
    if (!q || phase !== "reading") return;
    setFollowUp("");
    trackEvent("iching_followup_submit", { message_length: q.length });
    setActiveHao((idx) => idx);
    beginSection(q, false, lines);
  };

  const reset = () => {
    setPhase("ask");
    setQuestion("");
    setLines([]);
    setDrawnCount(0);
    setActiveHao(null);
    setScrollSections([]);
    setFollowUp("");
    setCharge(0);
    setBienRevealed(false);
    setHexCracking(false);
    setSessionId(null);
    setBackendHexagram(null);
    setCastAt(nowCastAt());
    setCastAtTouched(false);
    finalLinesRef.current = null;
    setFocusAnswer(false);
    if (user?.profile) {
      setDivinerId(getDefaultDivinerId(user.profile));
    } else {
      setDivinerId("cu_an");
    }
  };

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col overflow-hidden ${shake ? "animate-camera-shake" : ""}`}
    >
      <main className="min-h-0 flex-1 overflow-hidden">
        <div className="flex h-full w-full flex-col lg:grid lg:min-h-0 lg:grid-cols-[1fr_1.1fr]">
          {/* LEFT: ritual stage */}
          <section
            className={`iching-stage relative min-h-0 overflow-hidden lg:border-r border-gold-soft transition-all duration-500 lg:h-full ${phase === "ask" ? "iching-stage--ask" : ""} ${
              phase === "reading"
                ? focusAnswer
                  ? "h-0 opacity-0 pointer-events-none lg:h-full lg:opacity-100 lg:pointer-events-auto border-none"
                  : isMetaCollapsed
                    ? "h-[105px] shrink-0 border-b"
                    : "h-[45%] shrink-0 border-b"
                : "h-full shrink-0"
            }`}
          >
            {/* mystical fog */}
            <div className="iching-fog pointer-events-none absolute inset-0 opacity-60" />

            {/* Stage wrapper for absolute children */}
            <div className="relative h-full w-full min-h-0">
              {/* ASK STAGE */}
              <div
                data-tour="iching-ask-scroll-container"
                className={`absolute inset-0 overflow-y-auto transition-all duration-700 ease-in-out ${phase === "ask" ? "opacity-100 translate-y-0 scale-100 pointer-events-auto" : "opacity-0 translate-y-10 scale-95 pointer-events-none"}`}
              >
                {(phase === "ask" || phase === "casting") && (
                  <AskStage
                    question={question}
                    onChange={setQuestion}
                    divinerId={divinerId}
                    onDivinerChange={setDivinerId}
                    ichingSchool={ichingSchool}
                    onIchingSchoolChange={setIchingSchool}
                    castAt={castAt}
                    onCastAtChange={changeCastAt}
                    onUseCurrentCastAt={useCurrentCastAt}
                    onCast={cast}
                    charge={charge}
                    startCharge={startCharge}
                    releaseCharge={releaseCharge}
                  />
                )}
              </div>

              {/* CASTING STAGE */}
              <div
                className={`absolute inset-0 overflow-y-auto transition-all duration-700 ease-in-out ${phase === "casting" ? "opacity-100 translate-y-0 scale-100 pointer-events-auto" : "opacity-0 -translate-y-10 scale-95 pointer-events-none"}`}
              >
                {(phase === "casting" || phase === "ask" || phase === "reading") && (
                  <CastingStage coinsFlying={coinsFlying} />
                )}
              </div>

              {/* READING STAGE */}
              <div
                className={`absolute inset-0 overflow-y-auto transition-all duration-700 ease-in-out ${phase === "reading" ? "opacity-100 translate-y-0 scale-100 pointer-events-auto" : "opacity-0 translate-y-10 scale-95 pointer-events-none"}`}
              >
                {(phase === "reading" || phase === "casting") && (
                  <HexagramStage
                    lines={lines}
                    bienLines={bienLines}
                    bienHex={bienHex}
                    bienRevealed={bienRevealed}
                    hexCracking={hexCracking}
                    drawnCount={6}
                    activeHao={activeHao}
                    onSelectHao={(i) => setActiveHao(i)}
                    hex={hex}
                    backendHexagram={backendHexagram}
                    onReset={reset}
                    canShare={!!sessionId && !!backendHexagram}
                    onShare={() => setShareOpen(true)}
                    captureRef={hexShareRef}
                    canWallpaper={!!backendHexagram}
                    onWallpaper={() => setWallpaperOpen(true)}
                    isMetaCollapsed={isMetaCollapsed}
                    onToggleCollapse={() => setIsMetaCollapsed(!isMetaCollapsed)}
                  />
                )}
              </div>

              {sessionId && backendHexagram && (
                <ShareDialog
                  open={shareOpen}
                  onOpenChange={setShareOpen}
                  resourceType="iching"
                  resourceId={sessionId}
                  captureRef={hexShareRef}
                />
              )}

              {wallpaperOpen && backendHexagram && (
                <WallpaperDialog
                  open={wallpaperOpen}
                  onOpenChange={setWallpaperOpen}
                  hexagram={backendHexagram}
                  sessionId={sessionId}
                />
              )}

              {/* DRAWING PREVIEW */}
              <div
                className={`absolute inset-0 transition-all duration-700 ease-in-out ${phase === "casting" ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"}`}
              >
                {(phase === "casting" || phase === "reading") && (
                  <DrawingPreview lines={lines} drawnCount={drawnCount} />
                )}
              </div>
            </div>
          </section>

          {/* RIGHT: silk scroll panel */}
          <section
            className={`iching-info-panel relative min-h-0 overflow-hidden transition-all duration-500 lg:h-full lg:translate-y-0 ${
              phase === "reading"
                ? focusAnswer
                  ? "h-full flex-1 translate-y-0"
                  : isMetaCollapsed
                    ? "flex-1 min-h-0 translate-y-0"
                    : "h-[55%] shrink-0 translate-y-0"
                : "h-0 shrink-0 translate-y-10 lg:h-full lg:translate-y-0"
            }`}
          >
            {phase !== "reading" ? (
              <div
                className={`relative flex h-full flex-col justify-center pl-8 pr-8 py-10 lg:pl-32 lg:pr-24 lg:py-24 transition-all duration-700 ease-in-out ${phase === "casting" ? "opacity-0 blur-md scale-95 pointer-events-none" : "opacity-100 delay-300"} hidden lg:flex`}
              >
                {/* Background Watermark */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.02] text-primary pointer-events-none select-none mix-blend-plus-lighter">
                  <YinYang className="w-[1000px] h-[1000px]" />
                </div>

                {/* Group wrapper to perfectly center all items vertically */}
                <div className="relative z-10 w-full translate-y-6">
                  {/* TOP: Quote */}
                  <div className="max-w-lg">
                    <div className="relative z-10">
                      <span className="absolute -top-10 -left-4 font-serif-soft text-[110px] leading-none text-primary/20 select-none z-[-1] italic">
                        "
                      </span>
                      <h2 className="font-serif-soft text-[20px] font-light italic text-gold-gradient leading-[1.8] drop-shadow-sm">
                        Đạo của Vũ Trụ nằm ở sự Biến Thiên. Kinh Dịch không đoán định tử sinh, mà
                        chỉ lối cho người biết nương theo thời cuộc.
                      </h2>
                    </div>
                    <p className="mt-6 text-[13px] leading-[1.8] text-foreground/65 font-sans text-justify max-w-[90%] relative z-10 dark:text-muted-foreground/70">
                      Kinh Dịch (I Ching) là cẩm nang thông thái 3000 năm tuổi của Phương Đông. Dựa
                      trên sự biến đổi của Âm Dương, 64 quẻ Dịch sẽ giúp bạn nhìn thấu bản chất sự
                      việc hiện tại và dự báo chiều hướng tương lai để đưa ra quyết định sáng suốt.
                    </p>
                  </div>

                  {/* MIDDLE: 3 Steps */}
                  <div className="mt-12 flex flex-col gap-9 max-w-[480px]">
                    <div className="flex items-start gap-6 group">
                      <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold-soft/35 bg-card/80 text-primary/85 shadow-sm transition-all group-hover:scale-110 group-hover:border-primary/50 group-hover:text-primary dark:bg-background/50">
                        <Flame className="h-[20px] w-[20px]" />
                      </div>
                      <div>
                        <h4 className="font-display text-[11px] tracking-[0.25em] font-bold text-primary/90 mt-0.5">
                          BƯỚC 1: KHỞI TÂM (LẬP Ý)
                        </h4>
                        <p className="mt-2 text-[13px] text-foreground/68 leading-[1.8] dark:text-muted-foreground/75">
                          Viết rõ ràng một câu hỏi cụ thể, chỉ tập trung vào một sự việc (VD: "Dự án
                          sắp tới có thuận lợi không?"). Tránh hỏi chung chung.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-6 group">
                      <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold-soft/35 bg-card/80 text-primary/85 shadow-sm transition-all group-hover:scale-110 group-hover:border-primary/50 group-hover:text-primary dark:bg-background/50">
                        <YinYang className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-display text-[11px] tracking-[0.25em] font-bold text-primary/90 mt-0.5">
                          BƯỚC 2: TỤ KHÍ (GIAO CẢM)
                        </h4>
                        <p className="mt-2 text-[13px] text-foreground/68 leading-[1.8] dark:text-muted-foreground/75">
                          Gạt bỏ tạp niệm. Nhấn và{" "}
                          <strong className="text-primary font-medium">GIỮ</strong> chuột vào vòng
                          Thái Cực bên trái để truyền năng lượng của bạn vào hệ thống.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-6 group">
                      <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold-soft/35 bg-card/80 text-primary/85 shadow-sm transition-all group-hover:scale-110 group-hover:border-primary/50 group-hover:text-primary dark:bg-background/50">
                        <ScrollText className="h-[20px] w-[20px]" />
                      </div>
                      <div>
                        <h4 className="font-display text-[11px] tracking-[0.25em] font-bold text-primary/90 mt-0.5">
                          BƯỚC 3: KHAI QUẺ (LUẬN GIẢI)
                        </h4>
                        <p className="mt-2 text-[13px] text-foreground/68 leading-[1.8] dark:text-muted-foreground/75">
                          Cổ nhân sẽ dựa trên Quẻ Chủ (Hiện tại) và Hào Động (Biến cố) để mở ra cuộn
                          lụa thiên cơ, trao cho bạn lời khuyên đích đáng.
                        </p>
                        {/* Giá do BE quyết (`ICHING_READING_PRICE`, `PRICE_RATE`) — đổi env
                            thì sửa câu này theo. */}
                        <p className="mt-2 text-[12px] text-primary/80">
                          Phí luận giải: 5.000đ mỗi lần gieo quẻ · hỏi thêm 1đ/token.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* BOTTOM: Scroll Invocation */}
                  <div className="mt-12 text-center opacity-40 hover:opacity-70 transition-opacity flex flex-col items-center max-w-[480px]">
                    <span className="font-display text-[9px] tracking-[0.5em] text-primary">
                      CUỘN LỤA HUYỀN BÍ
                    </span>
                    <p className="mt-2 font-serif-soft text-lg italic text-gold-gradient">
                      Tâm thành thì lụa mới mở
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative h-full min-h-0">
                <div
                  ref={scrollRef}
                  data-tour="iching-result-scroll-container"
                  className="absolute inset-x-0 bottom-[calc(81px_+_env(safe-area-inset-bottom))] top-0 touch-pan-y overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-6 lg:px-10 animate-silk-unroll"
                >
                  {/* Dải công cụ ở ĐẦU bảng luận — CỐ Ý KHÔNG `sticky` (chủ dự
                      án chốt: không cần neo theo khi cuộn). Trên mobile khối quẻ
                      bên trái bị thu gọn và hàng nút của nó nằm dưới màn hình,
                      nên CẢ 4 nút nằm ở đây và hàng nút của `HexagramStage`
                      chuyển thành desktop-only (`hidden lg:flex`) để mobile
                      không có hai hàng trùng nhau.

                      Bốn ô `flex-1` BẰNG NHAU, dải trải kín chiều rộng: căn đều
                      hai bên mà vẫn cân đối. `justify-between` với các nút rộng
                      chênh nhau (32px so với 88px) thì nhìn rời rạc — đã thử.

                      ⚠️ Nhãn "Mở rộng"/"Thu gọn" đổi theo trạng thái nên chữ
                      phải nằm trong span BỀ RỘNG CỐ ĐỊNH + `whitespace-nowrap`.
                      Đo thật: "Mở rộng" 48px, "Thu gọn" 47px — ghim 44px là chữ
                      XUỐNG HAI DÒNG (và `scrollWidth` không bắt được lỗi này,
                      chỉ chiều cao mới thấy). */}
                  <div className="mb-2 flex w-full items-stretch divide-x divide-gold-soft/40 overflow-hidden rounded-full border border-gold-soft/60 bg-background/80 shadow-sm backdrop-blur lg:hidden">
                    <button
                      type="button"
                      onClick={reset}
                      title="Lập đàn lại"
                      className={ICHING_TOOLBAR_SEG}
                    >
                      <RotateCcw className="h-3.5 w-3.5 shrink-0" />
                      <span className="whitespace-nowrap">Lập lại</span>
                    </button>
                    {sessionId && backendHexagram && (
                      <>
                        <button
                          type="button"
                          onClick={() => setShareOpen(true)}
                          title="Chia sẻ quẻ"
                          className={ICHING_TOOLBAR_SEG}
                        >
                          <Share2 className="h-3.5 w-3.5 shrink-0" />
                          <span className="whitespace-nowrap">Chia sẻ</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setWallpaperOpen(true)}
                          title="Tạo ảnh nền từ quẻ"
                          className={ICHING_TOOLBAR_SEG}
                        >
                          <Wallpaper className="h-3.5 w-3.5 shrink-0" />
                          <span className="whitespace-nowrap">Ảnh nền</span>
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => setFocusAnswer((prev) => !prev)}
                      className={ICHING_TOOLBAR_SEG}
                    >
                      {focusAnswer ? (
                        <Eye className="h-3.5 w-3.5 shrink-0" />
                      ) : (
                        <EyeOff className="h-3.5 w-3.5 shrink-0" />
                      )}
                      <span className="inline-block w-[52px] whitespace-nowrap text-center">
                        {focusAnswer ? "Thu gọn" : "Mở rộng"}
                      </span>
                    </button>
                  </div>

                  <div
                    data-tour="iching-luangiai"
                    className="silk-paper relative mx-auto max-w-2xl rounded-md p-4 sm:p-6 lg:p-10"
                  >
                    {/* Triện cổ ở góc trên phải */}
                    <div className="absolute top-4 right-4 animate-seal-stamp">
                      <div className="seal-chop">
                        <span className="seal-chop-text">印</span>
                      </div>
                    </div>

                    <div data-tour="iching-luangiai-header" className="relative text-center">
                      <span className="font-display text-[10px] tracking-[0.4em] text-primary/80">
                        QUẺ CHỦ
                      </span>
                      <h2 className="mt-4 font-serif-soft text-3xl md:text-4xl italic text-gold-gradient drop-shadow-[0_2px_8px_rgba(229,184,105,0.25)]">
                        {backendHexagram?.que_chinh?.ten_tuong ||
                          backendHexagram?.que_chinh?.ten ||
                          hex?.vi ||
                          "QUẺ"}
                      </h2>
                      <p className="mt-3 font-serif-soft text-base italic text-foreground/85">
                        {backendHexagram?.que_chinh?.ten
                          ? [
                              backendHexagram.que_chinh.ten,
                              backendHexagram.que_chinh.cung_que,
                              backendHexagram.que_chinh.ngu_hanh,
                            ]
                              .filter(Boolean)
                              .join(" · ")
                          : hex?.meaning}
                      </p>
                      <div className="mx-auto mt-4 h-px w-32 bg-gold-gradient" />
                    </div>

                    {backendHexagram && <BackendHexagramDetails result={backendHexagram} />}

                    {/* Question echo */}
                    <div className="mt-6 rounded-md border border-gold-soft/50 bg-card/70 p-3 md:p-4 text-center shadow-sm dark:bg-background/30">
                      <span className="font-display text-[9px] tracking-[0.3em] text-primary/80">
                        TÂM SỰ NGƯỜI HỎI
                      </span>
                      <p className="mt-2 font-serif-soft text-base italic text-foreground/90">
                        "{question}"
                      </p>
                    </div>

                    {/* Sections */}
                    {scrollSections.map((s, idx) => (
                      <div key={s.id} className="mt-8">
                        {idx > 0 && (
                          <div className="my-6 flex items-center gap-3">
                            <div className="h-px flex-1 bg-gold-soft/60" />
                            <span className="font-display text-[9px] tracking-[0.3em] text-primary/70">
                              THỈNH GIÁO TIẾP
                            </span>
                            <div className="h-px flex-1 bg-gold-soft/60" />
                          </div>
                        )}
                        {idx > 0 && (
                          <div className="mb-4 rounded border border-gold-soft/40 bg-background/20 p-3">
                            <p className="font-serif-soft text-sm italic text-foreground/80">
                              "{s.question}"
                            </p>
                          </div>
                        )}
                        <div
                          className="prose prose-sm max-w-none font-sans font-normal text-sm leading-[1.8] text-foreground/88 lg:text-[15.5px] [&_blockquote]:text-foreground/82 [&_li]:text-foreground/88 [&_p]:text-foreground/88
                            [&_strong]:font-normal [&_strong]:text-primary [&_h1]:font-normal [&_h2]:font-normal [&_h3]:font-normal [&_h4]:font-normal
                            prose-headings:font-display prose-headings:tracking-wider prose-headings:text-primary
                            prose-em:text-foreground/75 prose-p:mb-8 prose-headings:mt-12 prose-headings:mb-6 dark:text-[#EAE0D5] dark:[&_blockquote]:text-[#EAE0D5] dark:[&_li]:text-[#EAE0D5] dark:[&_p]:text-[#EAE0D5] dark:prose-em:text-[#EAE0D5]/90"
                        >
                          {s.body ? (
                            <AnswerMarkdown body={s.body} highlightFirstParagraph={idx === 0} />
                          ) : (
                            <div className="flex items-center justify-center py-8">
                              <YinYang className="w-8 h-8 text-primary animate-bagua" />
                            </div>
                          )}
                        </div>
                        {s.failed && (
                          /* Lượt hỏi chưa có lời đáp — thường vì số dư không đủ. Nút này
                             là ĐƯỜNG DUY NHẤT để luận giải lại đúng quẻ ấy sau khi nạp
                             tiền; gieo lại quẻ mới là một quẻ khác. */
                          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => retrySection(s)}
                              className="inline-flex items-center gap-2 rounded-full border border-gold-soft bg-gold-gradient px-5 py-2 font-display text-[11px] tracking-[0.2em] text-primary-foreground shadow-gold transition hover:brightness-110"
                            >
                              <RotateCcw className="h-3.5 w-3.5 shrink-0" />
                              {s.isPrimary ? "LUẬN GIẢI QUẺ" : "THỬ LẠI"}
                            </button>
                            <Link
                              to="/thanh-toan"
                              className="font-display text-[11px] tracking-[0.2em] text-primary/80 underline decoration-dotted underline-offset-4 hover:text-primary"
                            >
                              NẠP THÊM
                            </Link>
                          </div>
                        )}
                        {s.ready && s.body && !s.failed ? <AiWatermark className="mt-4" /> : null}
                        {s.ready && s.messageId && s.body ? (
                          <div className="mt-3">
                            <StarRating
                              value={s.rating ?? 0}
                              busy={ratingBusyId === s.messageId}
                              onRate={(v) => void rateSection(s.messageId!, v)}
                              comment={s.ratingComment}
                              commentRewarded={Boolean(s.ratingCommentRewardedAt)}
                              onComment={(comment) =>
                                void rateSection(s.messageId!, s.rating ?? null, comment)
                              }
                            />
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Follow-up input */}
                <form
                  onSubmit={sendFollowUp}
                  data-tour="iching-chat"
                  className="iching-result-dock absolute inset-x-0 bottom-0 border-t border-gold-soft px-4 pt-3 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] backdrop-blur sm:px-6 lg:px-10"
                >
                  <div className="mx-auto flex max-w-2xl items-center gap-3 rounded-full border border-gold-soft bg-card/75 px-4 py-2 shadow-sm focus-within:border-primary/70 dark:bg-background/60 dark:shadow-gold">
                    <input
                      value={followUp}
                      onChange={(e) => setFollowUp(clampWords(e.target.value))}
                      placeholder={`Xin thỉnh giáo thêm về biến cố${activeHao !== null ? ` Hào ${activeHao + 1}` : ""}...`}
                      className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:italic placeholder:text-muted-foreground/70"
                    />
                    <button
                      type="submit"
                      disabled={!followUp.trim()}
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold-gradient text-primary-foreground shadow-gold transition hover:scale-110 disabled:opacity-50"
                      aria-label="Thỉnh giáo"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function AnswerMarkdown({
  body,
  highlightFirstParagraph = false,
}: {
  body: string;
  highlightFirstParagraph?: boolean;
}) {
  let paragraphIndex = 0;

  return (
    <ReactMarkdown
      remarkPlugins={MARKDOWN_REMARK_PLUGINS}
      components={{
        p: ({ children }) => {
          const shouldHighlight = highlightFirstParagraph && paragraphIndex === 0;
          paragraphIndex += 1;

          return (
            <p
              data-tour={shouldHighlight ? "iching-luangiai-summary" : undefined}
              className="mb-6 leading-[1.7] text-[#EAE0D5]"
            >
              {children}
            </p>
          );
        },
        strong: ({ children }) => (
          <strong className="font-normal text-[#D4AF37]">{children}</strong>
        ),
        ul: ({ children }) => (
          <ul className="flex flex-col gap-4 my-8 list-none !pl-0 !ml-0">{children}</ul>
        ),
        li: ({ children }) => (
          <li className="flex gap-4 items-start !pl-0 !ml-0 !my-0 leading-[1.7]">
            <span className="text-[#D4AF37] text-[13px] mt-[4px] opacity-80 select-none">✦</span>
            <div className="flex-1">{children}</div>
          </li>
        ),
        h3: ({ children }) => (
          <>
            <div className="my-14 flex items-center justify-center gap-4 opacity-50 select-none">
              <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-[#D4AF37]/60" />
              <span className="text-[#D4AF37] text-[9px] tracking-[0.4em]">♦ ♦ ♦</span>
              <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-[#D4AF37]/60" />
            </div>
            <h3 className="font-normal text-[#D4AF37] mb-6 mt-10">{children}</h3>
          </>
        ),
        h2: ({ children }) => (
          <>
            <div className="my-14 flex items-center justify-center gap-4 opacity-50 select-none">
              <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-[#D4AF37]/60" />
              <span className="text-[#D4AF37] text-[9px] tracking-[0.4em]">♦ ♦ ♦</span>
              <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-[#D4AF37]/60" />
            </div>
            <h2 className="font-normal text-[#D4AF37] mb-6 mt-10">{children}</h2>
          </>
        ),
      }}
    >
      {body}
    </ReactMarkdown>
  );
}

function HexagramHaoList({ title, haoList }: { title: string; haoList: LucHao[] }) {
  if (!haoList?.length) return null;
  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        <div className="h-px flex-1 bg-gold-soft/30" />
        <span className="font-display text-[11px] uppercase tracking-[0.32em] text-primary font-medium text-center">
          {title}
        </span>
        <div className="h-px flex-1 bg-gold-soft/30" />
      </div>
      <div className="space-y-1">
        {haoList
          .slice()
          .sort((a, b) => b.vi_tri - a.vi_tri)
          .map((hao) => (
            <div
              key={hao.vi_tri}
              className={`rounded-lg border-b border-gold-soft/15 px-2 py-3 text-[13px] text-foreground/86 last:border-b-0 dark:text-[#EFE6D5] ${
                hao.bien ? "bg-[oklch(0.93_0.03_190/0.45)] dark:bg-[oklch(0.2_0.055_190/0.25)]" : ""
              }`}
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span
                  className={`font-display font-medium ${hao.bien ? "iching-accent" : "text-primary"}`}
                >
                  Hào {hao.vi_tri}
                </span>
                <span>
                  {hao.loai}
                  {hao.bien ? <span className="iching-accent font-semibold"> động</span> : " tĩnh"}
                </span>
                {hao.luc_than && (
                  <span>
                    Lục thân:{" "}
                    <span className="font-medium text-foreground/80 dark:text-[#D1C7BD]">
                      {hao.luc_than}
                    </span>
                  </span>
                )}
                {hao.can_chi && (
                  <span>
                    Can chi:{" "}
                    <span className="font-medium text-foreground/80 dark:text-[#D1C7BD]">
                      {hao.can_chi}
                    </span>
                  </span>
                )}
                {hao.the_ung && (
                  <span className="font-bold text-primary tracking-wider">
                    {hao.the_ung.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-foreground/68 text-[12px] dark:text-[#D1C7BD]/80">
                {hao.phuc_than && <span>Phục thần: {hao.phuc_than}</span>}
                {hao.luc_thu && <span>Lục thú: {hao.luc_thu}</span>}
                {hao.vuong_suy && <span>Vượng suy: {hao.vuong_suy}</span>}
                {hao.tk && <span>Tuần không: {hao.tk}</span>}
                {hao.quat_than && <span>Quái thân: {hao.quat_than}</span>}
                {hao.loc && <span>Lộc: {hao.loc}</span>}
                {hao.ma && <span>Mã: {hao.ma}</span>}
                {hao.quy && <span>Quý: {hao.quy}</span>}
                {hao.dao && <span>Đào: {hao.dao}</span>}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function BackendHexagramDetails({ result }: { result: HexagramResult }) {
  const main = result.que_chinh;
  const changed = result.que_bien;
  const time = result.thoi_gian;

  return (
    <div className="iching-detail-card mt-6 space-y-6 rounded-xl border p-6 backdrop-blur-md">
      <div className="grid text-left sm:grid-cols-2 sm:gap-x-8">
        <InfoRow
          label="Quẻ chủ"
          value={main ? `${main.ten}${main.ten_tuong ? ` — ${main.ten_tuong}` : ""}` : undefined}
        />
        <InfoRow label="Số quẻ" value={main?.so_que ? String(main.so_que) : undefined} />
        <InfoRow label="Cung quẻ" value={main?.cung_que} />
        <InfoRow label="Ngũ hành" value={main?.ngu_hanh} />
        <InfoRow
          label="Quẻ biến"
          value={
            changed
              ? `${changed.ten}${changed.ten_tuong ? ` — ${changed.ten_tuong}` : ""}`
              : "Không có"
          }
        />
        <InfoRow label="Hào động" value={result.hao_dong ? `Hào ${result.hao_dong}` : "Không có"} />
        <InfoRow label="Thượng quái" value={main?.ten_thuong_bat_quai} />
        <InfoRow label="Hạ quái" value={main?.ten_ha_bat_quai} />
      </div>

      {time && (
        <div className="border-t border-gold-soft/30 pt-5">
          <div className="mb-4 flex items-center gap-4">
            <div className="h-px flex-1 bg-gold-soft/30" />
            <span className="font-display text-[11px] uppercase tracking-[0.32em] text-primary font-medium text-center">
              Thời gian gieo quẻ
            </span>
            <div className="h-px flex-1 bg-gold-soft/30" />
          </div>
          <div className="grid text-[13px] text-foreground/86 sm:grid-cols-2 sm:gap-x-8 dark:text-[#EFE6D5]">
            <InfoRow label="Dương lịch" value={time.solar_datetime} compact />
            <InfoRow label="Âm lịch" value={time.lunar_date} compact />
            <InfoRow label="Giờ" value={time.can_chi_gio} compact />
            <InfoRow label="Ngày" value={time.can_chi_ngay} compact />
            <InfoRow label="Tháng" value={time.can_chi_thang} compact />
            <InfoRow label="Năm" value={time.can_chi_nam} compact />
            <InfoRow label="Tiết khí" value={time.tiet_khi} compact />
            <InfoRow
              label="Nhật/Nguyệt"
              value={[time.nhat_than, time.nguyet_lenh].filter(Boolean).join(" · ")}
              compact
            />
          </div>
        </div>
      )}

      {main?.luc_hao?.length || changed?.luc_hao?.length ? (
        <div className="border-t border-gold-soft/30 pt-5">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-12">
            <HexagramHaoList title="Lục hào Quẻ chủ" haoList={main?.luc_hao || []} />
            {changed && (
              <HexagramHaoList title="Lục hào Quẻ biến" haoList={changed?.luc_hao || []} />
            )}
          </div>
        </div>
      ) : null}

      <div className="mt-8 flex justify-center border-t border-gold-soft/20 pt-6">
        <Link
          to="/kinh-dich-chi-tiet"
          search={{ session_id: result.session_id }}
          className="inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/10 px-6 py-2.5 font-display text-xs tracking-widest text-primary shadow-sm transition hover:bg-primary/20"
        >
          <BookOpen className="h-4 w-4" />
          TÌM HIỂU KINH DỊCH
        </Link>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  compact,
}: {
  label: string;
  value?: string | null;
  compact?: boolean;
}) {
  if (!value) return null;

  return (
    <div
      className={`flex items-baseline justify-between gap-4 border-b border-gold-soft/20 py-2.5 last:border-b-0 ${compact ? "min-w-0" : ""}`}
    >
      <span className="font-display text-[10px] uppercase tracking-[0.16em] text-primary/90 shrink-0 font-medium">
        {label}
      </span>
      <span className="break-words text-right font-serif-soft text-[14px] text-foreground/88 dark:text-[#EFE6D5]">
        {value}
      </span>
    </div>
  );
}

// ---------- Thời điểm lập quẻ (Ngày/Giờ) ----------
type CastAt = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function nowCastAt(): CastAt {
  const d = new Date();
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** ISO giờ VN (naive wall-clock) để BE gắn timezone +7, vd "2026-06-20T10:17:00". */
function castAtToIso(c: CastAt): string {
  return `${c.year}-${pad2(c.month)}-${pad2(c.day)}T${pad2(c.hour)}:${pad2(c.minute)}:00`;
}

function formatCastAt(c: CastAt): string {
  return `${pad2(c.hour)}:${pad2(c.minute)} · ${pad2(c.day)}/${pad2(c.month)}/${c.year}`;
}

function castAtToDateInputValue(c: CastAt): string {
  return `${c.year}-${pad2(c.month)}-${pad2(c.day)}`;
}

function inputValueToCastDate(value: string, current: CastAt): CastAt | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: current.hour,
    minute: current.minute,
  };
}

type DialMode = "hour" | "minute";

function TimeDial({
  value,
  mode,
  onModeChange,
  onChange,
  disabled,
  compact = false,
}: {
  value: CastAt;
  mode: DialMode;
  onModeChange: (mode: DialMode) => void;
  onChange: (next: CastAt) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const size = compact ? 142 : 174;
  const center = size / 2;
  const radius = compact ? 53 : 66;
  const activeValue = mode === "hour" ? value.hour : value.minute;
  const units = mode === "hour" ? 24 : 60;
  const angle = (activeValue / units) * Math.PI * 2 - Math.PI / 2;
  const handX = center + Math.cos(angle) * radius;
  const handY = center + Math.sin(angle) * radius;
  const ticks = Array.from({ length: mode === "hour" ? 24 : 12 }, (_, index) => index);

  const updateFromPoint = (clientX: number, clientY: number, target: SVGSVGElement) => {
    const rect = target.getBoundingClientRect();
    const x = clientX - rect.left - rect.width / 2;
    const y = clientY - rect.top - rect.height / 2;
    const rawAngle = Math.atan2(y, x) + Math.PI / 2;
    const normalized = (rawAngle + Math.PI * 2) % (Math.PI * 2);

    if (mode === "hour") {
      onChange({ ...value, hour: Math.round((normalized / (Math.PI * 2)) * 24) % 24 });
      return;
    }

    const snappedMinute = Math.round((normalized / (Math.PI * 2)) * 12) * 5;
    onChange({ ...value, minute: snappedMinute % 60 });
  };

  return (
    <div className="flex flex-col items-center">
      <div
        className={`${compact ? "mb-1.5" : "mb-2"} inline-flex rounded-full border border-gold-soft/25 bg-background/45 p-1`}
      >
        {(["hour", "minute"] as DialMode[]).map((item) => (
          <button
            key={item}
            type="button"
            disabled={disabled}
            onClick={() => onModeChange(item)}
            className={`rounded-full px-3 py-1 text-[11px] font-medium transition ${
              mode === item
                ? "bg-primary/20 text-primary shadow-[0_0_14px_rgba(212,175,55,0.18)]"
                : "text-muted-foreground hover:text-primary"
            } disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {item === "hour" ? "Giờ" : "Phút"}
          </button>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${size} ${size}`}
        className={`${compact ? "h-[142px] w-[142px]" : "h-[174px] w-[174px]"} touch-none select-none ${disabled ? "opacity-60" : "cursor-grab active:cursor-grabbing"}`}
        onPointerDown={(event) => {
          if (disabled) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          updateFromPoint(event.clientX, event.clientY, event.currentTarget);
        }}
        onPointerMove={(event) => {
          if (disabled || event.buttons !== 1) return;
          updateFromPoint(event.clientX, event.clientY, event.currentTarget);
        }}
      >
        <defs>
          <radialGradient id="cast-time-dial-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
            <stop offset="68%" stopColor="currentColor" stopOpacity="0.06" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle
          cx={center}
          cy={center}
          r={compact ? 67 : 82}
          className="fill-primary/10 text-primary"
        />
        <circle cx={center} cy={center} r={compact ? 67 : 82} fill="url(#cast-time-dial-glow)" />
        <circle
          cx={center}
          cy={center}
          r={radius}
          className="fill-background/40 stroke-primary/35"
          strokeWidth="1"
        />
        {ticks.map((tick) => {
          const tickUnits = mode === "hour" ? 24 : 12;
          const tickAngle = (tick / tickUnits) * Math.PI * 2 - Math.PI / 2;
          const outer = radius + (tick % 3 === 0 ? 7 : 4);
          const inner = radius - (tick % 3 === 0 ? 7 : 3);
          const x1 = center + Math.cos(tickAngle) * inner;
          const y1 = center + Math.sin(tickAngle) * inner;
          const x2 = center + Math.cos(tickAngle) * outer;
          const y2 = center + Math.sin(tickAngle) * outer;
          return (
            <line
              key={tick}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              className={tick % 3 === 0 ? "stroke-primary/70" : "stroke-primary/25"}
              strokeWidth={tick % 3 === 0 ? 1.4 : 1}
              strokeLinecap="round"
            />
          );
        })}
        <line
          x1={center}
          y1={center}
          x2={handX}
          y2={handY}
          className="stroke-primary"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx={handX} cy={handY} r={7} className="fill-primary drop-shadow-md" />
        <circle
          cx={center}
          cy={center}
          r={compact ? 31 : 37}
          className="fill-background/80 stroke-gold-soft/25"
        />
        <text
          x={center}
          y={center - 2}
          textAnchor="middle"
          className={`${compact ? "text-[18px]" : "text-[21px]"} fill-primary font-serif-soft font-semibold`}
        >
          {pad2(value.hour)}:{pad2(value.minute)}
        </text>
        <text
          x={center}
          y={center + (compact ? 14 : 17)}
          textAnchor="middle"
          className="fill-muted-foreground/70 text-[8px] font-medium uppercase tracking-[0.24em]"
        >
          {mode === "hour" ? "an giờ" : "an phút"}
        </text>
      </svg>
    </div>
  );
}

function CastTimePicker({
  value,
  onChange,
  onUseCurrent,
  disabled,
}: {
  value: CastAt;
  onChange: (next: CastAt) => void;
  onUseCurrent: () => void;
  disabled?: boolean;
}) {
  const currentMomentLabel = formatCastAt(value);
  const dateInputValue = castAtToDateInputValue(value);
  const [open, setOpen] = useState(false);
  const [dialMode, setDialMode] = useState<DialMode>("hour");
  const isMobile = useIsMobile();
  const wrapRef = useRef<HTMLDivElement>(null);

  // Desktop: panel mở inline (không qua Radix portal). Tự đóng khi click ra
  // ngoài hoặc nhấn Escape — tránh việc DismissableLayer của Popover đóng panel
  // ngay khi click vào (bug "bị tắt khi click vô").
  useEffect(() => {
    if (isMobile || !open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isMobile, open]);

  const triggerClassName =
    "group relative w-full overflow-hidden rounded-2xl border border-gold-soft/35 bg-background/25 px-3 py-2 text-left shadow-[0_0_26px_rgba(212,175,55,0.10)] backdrop-blur-md transition hover:border-primary/55 hover:bg-primary/[0.06] disabled:cursor-not-allowed disabled:opacity-60";

  const triggerInner = (
    <>
      <span className="pointer-events-none absolute -right-8 -top-10 h-20 w-20 rounded-full bg-primary/15 blur-2xl transition group-hover:bg-primary/25" />
      <span className="relative flex items-center justify-between gap-3">
        <span className="min-w-0">
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_12px_rgba(212,175,55,0.85)] animate-pulse" />
            <span className="font-display text-[9px] uppercase tracking-[0.18em] text-primary mobile-label-uppercase">
              Giờ động tâm
            </span>
          </span>
          <span className="mt-0.5 block truncate text-[11px] italic leading-snug text-muted-foreground/80">
            Dịch lấy thời làm gốc, an đúng lúc tâm khởi để mở quẻ.
          </span>
        </span>
        <span className="shrink-0 rounded-full border border-primary/20 bg-primary/[0.07] px-2.5 py-0.5 font-serif-soft text-xs font-semibold text-primary">
          {currentMomentLabel}
        </span>
      </span>
    </>
  );

  if (isMobile) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} className={triggerClassName}>
            {triggerInner}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="center"
          side="bottom"
          sideOffset={6}
          collisionPadding={14}
          // z-[10001] để panel nổi TRÊN overlay của tour (zIndex 10000) — nếu không, trong lúc chạy
          // hướng dẫn trên mobile panel bị overlay che mờ & chặn tương tác, không chỉnh giờ được.
          className="z-[10001] w-[328px] max-w-[calc(100vw-24px)] max-h-[calc(100vh-120px)] overflow-x-hidden overflow-y-auto rounded-2xl border-gold-soft/35 bg-[#fdfcf9] dark:bg-[oklch(0.12_0.05_295/0.98)] p-0 text-foreground shadow-[0_20px_80px_rgba(0,0,0,0.45),0_0_60px_rgba(212,175,55,0.10)] backdrop-blur-xl"
        >
          <div className="relative p-3.5">
            <div className="pointer-events-none absolute -left-14 -top-14 h-28 w-28 rounded-full bg-primary/15 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-14 -right-10 h-32 w-32 rounded-full dark:bg-[oklch(0.45_0.20_305/0.18)] bg-primary/10 blur-3xl" />

            <div className="relative flex items-start justify-between gap-3">
              <div>
                <p className="font-display text-[9px] uppercase tracking-[0.22em] text-primary">
                  Giờ động tâm
                </p>
                <p className="mt-0.5 text-[11px] italic leading-snug text-muted-foreground/85">
                  Dịch lấy thời làm gốc, an đúng lúc tâm khởi để mở quẻ.
                </p>
              </div>
              <button
                type="button"
                disabled={disabled}
                onClick={onUseCurrent}
                className="shrink-0 rounded-full border border-gold-soft/30 bg-background/35 px-3 py-1.5 font-serif-soft text-[11px] italic text-primary/85 transition hover:border-primary/60 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
              >
                Hiện tại
              </button>
            </div>

            <div className="relative mt-2.5 rounded-xl border border-primary/15 bg-primary/[0.05] px-2.5 py-1.5">
              <p className="text-[10.5px] leading-snug text-muted-foreground/85">
                Giờ động tâm là lúc câu hỏi vừa khởi lên trong lòng bạn; chọn càng đúng khoảnh khắc
                ấy, quẻ càng bám sát việc đang hỏi.
              </p>
            </div>

            <label className="relative mt-2.5 block">
              <span className="mb-1 block font-display text-[9px] uppercase tracking-[0.22em] text-primary/75">
                Ngày động tâm
              </span>
              <input
                aria-label="Ngày động tâm"
                type="date"
                value={dateInputValue}
                disabled={disabled}
                onChange={(event) => {
                  const next = inputValueToCastDate(event.target.value, value);
                  if (next) onChange(next);
                }}
                className="h-9 w-full rounded-full border border-primary/25 bg-background/45 px-3 text-sm font-semibold text-primary outline-none transition dark:[color-scheme:dark] focus:border-primary focus:bg-primary/[0.08] disabled:cursor-not-allowed disabled:opacity-60"
              />
            </label>

            <div className="relative mt-2.5 rounded-2xl border border-gold-soft/20 bg-background/25 py-2">
              <TimeDial
                value={value}
                mode={dialMode}
                onModeChange={setDialMode}
                onChange={onChange}
                disabled={disabled}
                compact
              />
            </div>

            <div className="relative mt-2 flex items-center justify-between gap-2 rounded-full border border-primary/15 bg-primary/[0.05] px-3 py-1.5">
              <span className="text-[10px] italic text-muted-foreground/70">Mốc đang an</span>
              <span className="font-serif-soft text-sm font-semibold text-primary">
                {currentMomentLabel}
              </span>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className={triggerClassName}
      >
        {triggerInner}
      </button>
      {open && (
        <div className="relative mt-2 overflow-hidden rounded-2xl border border-gold-soft/30 bg-background/25 p-3 text-left shadow-[0_0_26px_rgba(212,175,55,0.10)] backdrop-blur-md">
          <div className="pointer-events-none absolute -left-12 -top-16 h-28 w-28 rounded-full bg-primary/12 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 -right-10 h-32 w-32 rounded-full bg-[oklch(0.45_0.20_305/0.16)] blur-3xl" />

          <div className="relative grid grid-cols-[1fr_auto] gap-3">
            <div className="min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-[9px] uppercase tracking-[0.22em] text-primary">
                    Giờ động tâm
                  </p>
                  <p className="mt-0.5 text-[11px] italic leading-snug text-muted-foreground/85">
                    Dịch lấy thời làm gốc, an đúng lúc tâm khởi để mở quẻ.
                  </p>
                </div>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={onUseCurrent}
                  className="shrink-0 rounded-full border border-gold-soft/30 bg-background/35 px-3 py-1.5 font-serif-soft text-[11px] italic text-primary/85 transition hover:border-primary/60 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Hiện tại
                </button>
              </div>

              <div className="mt-2 rounded-xl border border-primary/15 bg-primary/[0.05] px-2.5 py-1.5">
                <p className="text-[10.5px] leading-snug text-muted-foreground/85">
                  Giờ động tâm là lúc câu hỏi vừa khởi lên trong lòng bạn; chọn càng đúng khoảnh
                  khắc ấy, quẻ càng bám sát việc đang hỏi.
                </p>
              </div>

              <label className="mt-2.5 block">
                <span className="mb-1 block font-display text-[9px] uppercase tracking-[0.22em] text-primary/75">
                  Ngày động tâm
                </span>
                <input
                  aria-label="Ngày động tâm"
                  type="date"
                  value={dateInputValue}
                  disabled={disabled}
                  onChange={(event) => {
                    const next = inputValueToCastDate(event.target.value, value);
                    if (next) onChange(next);
                  }}
                  className="h-9 w-full rounded-full border border-primary/25 bg-background/45 px-3 text-sm font-semibold text-primary outline-none transition [color-scheme:dark] focus:border-primary focus:bg-primary/[0.08] disabled:cursor-not-allowed disabled:opacity-60"
                />
              </label>

              <div className="mt-2 flex items-center justify-between gap-2 rounded-full border border-primary/15 bg-primary/[0.05] px-3 py-1.5">
                <span className="text-[10px] italic text-muted-foreground/70">Mốc đang an</span>
                <span className="font-serif-soft text-sm font-semibold text-primary">
                  {currentMomentLabel}
                </span>
              </div>
            </div>

            <div className="rounded-2xl border border-gold-soft/20 bg-background/25 px-2 py-2">
              <TimeDial
                value={value}
                mode={dialMode}
                onModeChange={setDialMode}
                onChange={onChange}
                disabled={disabled}
                compact
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- BƯỚC 1: ask stage with HOLD-TO-CHARGE ----------
const ICHING_SCHOOL_KEY = "boide_iching_school";

function AskStage({
  question,
  onChange,
  divinerId,
  onDivinerChange,
  ichingSchool,
  onIchingSchoolChange,
  castAt,
  onCastAtChange,
  onUseCurrentCastAt,
  onCast,
  charge,
  startCharge,
  releaseCharge,
}: {
  question: string;
  onChange: (s: string) => void;
  divinerId: string;
  onDivinerChange: (s: string) => void;
  ichingSchool: IchingSchool;
  onIchingSchoolChange: (s: IchingSchool) => void;
  castAt: CastAt;
  onCastAtChange: (next: CastAt) => void;
  onUseCurrentCastAt: () => void;
  onCast: () => void;
  charge: number;
  startCharge: () => void;
  releaseCharge: () => void;
}) {
  const samples = [
    "Dự án sắp tới có thành công không?",
    "Có nên chuyển công việc trong 3 tháng tới?",
    "Tình duyên của tôi sắp tới ra sao?",
    "Khoản đầu tư hiện tại có nên giữ?",
  ];
  const charging = charge > 0 && charge < 1;
  const hasQ = !!question.trim();
  const RADIUS = 64;
  const CIRC = 2 * Math.PI * RADIUS;

  return (
    <div
      className="iching-ask-stage relative flex min-h-full flex-col items-center justify-start sm:justify-center px-4 py-4 sm:px-6 sm:py-10 md:px-8 text-center"
      style={{ ["--charge" as string]: String(charge) }}
    >
      {/* Vignette darkens as user charges */}
      <div className="pointer-events-none absolute inset-0 -z-10 charge-vignette" />

      {/* Floating bagua at top - hidden on mobile to save space */}
      <div className="relative hidden xl:block">
        <div className="absolute inset-0 -z-10 rounded-full bg-primary/30 blur-[80px] animate-glow" />
        <YinYang className="h-16 w-16 2xl:h-20 2xl:w-20 text-primary animate-bagua" />
      </div>

      <span className="mt-1 xl:mt-3 font-display text-[9px] tracking-[0.25em] text-primary mobile-label-uppercase">
        LẬP ĐÀN — KHỞI TÂM
      </span>
      <h2 className="mt-1 max-w-xl font-serif-soft text-base sm:text-[17px] xl:text-lg italic leading-snug text-gold-gradient">
        Tâm thành tắc linh — viết câu hỏi rồi tụ khí thỉnh quẻ
      </h2>

      {/* Glowing question (the one being charged) */}
      <div data-tour="iching-question" className="relative mt-3 xl:mt-4 w-full max-w-lg">
        <div
          className={`absolute inset-0 -z-10 rounded-full blur-2xl transition-opacity duration-500 ${hasQ ? "bg-primary/20 opacity-100" : "bg-primary/5 opacity-50"}`}
        />
        <input
          value={question}
          onChange={(e) => onChange(clampWords(e.target.value))}
          placeholder="Dự án sắp tới có thành công không?"
          className={`w-full rounded-full border bg-background/50 px-5 py-3 sm:px-6 sm:py-4 text-center font-serif-soft text-base sm:text-[17px] outline-none backdrop-blur-md transition-all duration-500 placeholder:italic placeholder:text-muted-foreground/50 ${
            charging || charge >= 1
              ? "charge-question border-gold-soft text-foreground"
              : hasQ
                ? "border-gold-soft text-foreground shadow-[0_0_15px_rgba(212,175,55,0.25)]"
                : "border-primary/50 shadow-[0_0_20px_rgba(212,175,55,0.15)] animate-pulse hover:border-primary/80"
          } py-3 xl:py-3`}
        />
      </div>

      <div data-tour="iching-casttime" className="mt-3 w-full max-w-lg">
        <CastTimePicker
          value={castAt}
          onChange={onCastAtChange}
          onUseCurrent={onUseCurrentCastAt}
          disabled={charging || charge >= 1}
        />
      </div>

      <div data-tour="iching-diviner" className="iching-diviner-wrap mt-2.5 w-full max-w-lg">
        <DivinerPicker
          value={divinerId}
          onChange={onDivinerChange}
          disabled={charging || charge >= 1}
          ritualCard
        />
      </div>

      <div className="iching-diviner-wrap mt-2.5 w-full max-w-lg">
        <IchingSchoolPicker
          value={ichingSchool}
          onChange={onIchingSchoolChange}
          disabled={charging || charge >= 1}
        />
      </div>

      {/* Group Bát Quái, suggestions, tips, and mobile button for unified tour highlighting */}
      <div data-tour="iching-cast" className="mt-2.5 sm:mt-3 flex flex-col items-center w-full">
        {/* HOLD-TO-CHARGE Bagua button */}
        <div className="iching-cast-control relative flex flex-col items-center scale-90 sm:scale-90 xl:scale-95 2xl:scale-100">
          <button
            type="button"
            disabled={!hasQ}
            onMouseDown={startCharge}
            onMouseUp={releaseCharge}
            onMouseLeave={releaseCharge}
            onTouchStart={(e) => {
              e.preventDefault();
              startCharge();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              releaseCharge();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && hasQ) onCast();
            }}
            className={`group relative flex h-28 w-28 sm:h-32 sm:w-32 xl:h-36 xl:w-36 2xl:h-40 2xl:w-40 items-center justify-center rounded-full transition select-none ${
              hasQ ? "cursor-pointer" : "cursor-not-allowed opacity-50"
            }`}
            aria-label="Nhấn giữ để tụ khí thỉnh quẻ"
          >
            {/* Particles flowing in (only while charging) */}
            {charging && (
              <>
                {Array.from({ length: 14 }).map((_, i) => {
                  const angle = (i / 14) * Math.PI * 2;
                  const dist = 90 + (i % 3) * 14;
                  return (
                    <span
                      key={i}
                      className="charge-particle"
                      style={{
                        ["--px" as string]: `${Math.cos(angle) * dist}px`,
                        ["--py" as string]: `${Math.sin(angle) * dist}px`,
                        ["--pd" as string]: `${(i * 0.08).toFixed(2)}s`,
                      }}
                    />
                  );
                })}
              </>
            )}

            {/* Ready Glow Aura */}
            {hasQ && !charging && charge === 0 && (
              <div className="absolute inset-[-20px] sm:inset-[-25px] rounded-full bg-primary/20 blur-xl animate-pulse" />
            )}

            {/* Outer glow ring */}
            <span
              className="absolute inset-0 rounded-full blur-xl transition-all duration-500"
              style={{
                background: hasQ
                  ? `radial-gradient(circle, oklch(0.82 0.14 85 / ${0.5 + charge * 0.5}), transparent 75%)`
                  : `radial-gradient(circle, oklch(0.82 0.14 85 / 0.1), transparent 60%)`,
              }}
            />

            {/* SVG charge progress ring */}
            <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 160 160">
              <circle
                cx="80"
                cy="80"
                r={RADIUS}
                fill="none"
                stroke={hasQ ? "oklch(0.82 0.14 85 / 0.4)" : "oklch(0.82 0.14 85 / 0.15)"}
                strokeWidth="2"
                className="transition-colors duration-500"
              />
              <circle
                cx="80"
                cy="80"
                r={RADIUS}
                fill="none"
                stroke="oklch(0.92 0.16 85)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={CIRC}
                strokeDashoffset={CIRC * (1 - charge)}
                style={{
                  transition: "stroke-dashoffset 0.05s linear",
                  filter: `drop-shadow(0 0 ${4 + charge * 14}px oklch(0.82 0.14 85 / ${0.5 + charge * 0.5}))`,
                }}
              />
            </svg>

            {/* Cùng nét mực Âm Dương dùng trên app. */}
            <img
              src="/ritual/kinh-dich-yin-yang-ink.webp"
              alt=""
              className={`iching-cast-symbol ${charging ? "bagua-charging" : "animate-bagua"}`}
            />

            {/* Inner pulsing core when fully charged */}
            {charge >= 1 && (
              <span className="absolute inset-6 rounded-full bg-primary/40 blur-xl animate-glow" />
            )}
          </button>

          <p className="mt-2.5 font-display text-[9px] sm:text-[10px] tracking-[0.32em] sm:tracking-[0.4em] text-primary/80">
            {!hasQ
              ? "VIẾT CÂU HỎI TRƯỚC ĐÃ"
              : charging
                ? "ĐANG TỤ KHÍ — GIỮ CHẶT TÂM Ý..."
                : charge >= 1
                  ? "KHÍ ĐÃ ĐỦ — THIÊN CƠ HIỂN HIỆN"
                  : "ẤN VÀ GIỮ CHUỘT — TỤ KHÍ THỈNH QUẺ"}
          </p>
        </div>

        {/* Explicit tap button for mobile users */}
        {hasQ && !charging && charge === 0 && (
          <div className="mt-3.5 sm:hidden">
            <button
              type="button"
              onClick={onCast}
              className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-7 py-2.5 text-sm font-semibold text-primary-foreground shadow-gold transition active:scale-95 animate-pulse"
            >
              <Sparkles className="h-4 w-4" />
              Gieo quẻ ngay
            </button>
          </div>
        )}

        {/* Suggestions stack wrapping instead of horizontal cutoff */}
        <div className="mt-2.5 sm:mt-3 w-full max-w-lg">
          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2.5">
            {samples.map((q, index) => (
              <button
                key={q}
                type="button"
                onClick={() => onChange(q)}
                className={`rounded-full border border-primary/20 bg-background/30 px-3 py-1.5 text-[11px] sm:text-[12px] italic text-muted-foreground/80 transition-all duration-300 hover:border-primary/80 hover:text-primary hover:bg-primary/10 hover:shadow-[0_0_12px_rgba(212,175,55,0.15)] ${index > 1 ? "sm:hidden" : ""}`}
              >
                "{q}"
              </button>
            ))}
          </div>
        </div>

        {/* Compact helper */}
        <div className="mt-3 sm:mt-2.5 inline-flex max-w-md items-center gap-2 rounded-full border border-gold-soft/40 bg-background/30 px-4 py-2 sm:py-1.5 text-[11px] italic text-muted-foreground backdrop-blur-sm">
          <Sparkles className="h-3 w-3 text-primary animate-pulse" />
          <span className="hidden sm:inline">
            Mẹo: <span className="text-primary">Ấn và GIỮ chuột</span> trên Bát Quái để tụ khí — thả
            ra khi vòng sáng đầy.
          </span>
          <span className="sm:hidden text-xs">
            Mẹo: Ấn giữ Bát quái hoặc chạm <span className="text-primary">"Gieo quẻ ngay"</span> để
            thỉnh quẻ.
          </span>
        </div>
      </div>

      <div className="mt-3 sm:mt-2.5 opacity-80 transition-opacity hover:opacity-100">
        <Link
          to="/kinh-dich-chi-tiet"
          className="inline-flex items-center gap-2 rounded-full border border-gold-soft/30 bg-background/20 px-5 py-2 sm:py-1.5 font-display text-[11px] tracking-widest text-[#E5B869] backdrop-blur transition hover:border-[#E5B869]/60 hover:bg-[#E5B869]/10"
        >
          <BookOpen className="h-3.5 w-3.5" />
          TÌM HIỂU KINH DỊCH
        </Link>
      </div>
    </div>
  );
}

// ---------- BƯỚC 2: casting ritual ----------
function CastingStage({ coinsFlying }: { coinsFlying: boolean }) {
  return (
    <div className="relative flex h-full items-center justify-center">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full animate-purple-mist" />
      <div className="relative animate-shell-rise">
        <div className="absolute inset-0 -z-10 rounded-full bg-primary/40 blur-3xl animate-glow" />
        <YinYang className="w-[160px] h-[160px] text-primary animate-bagua" />
      </div>

      {coinsFlying && (
        <>
          {[
            { mx: "-80px", fx: "-110px", fy: "120px", delay: "0ms" },
            { mx: "0px", fx: "0px", fy: "140px", delay: "80ms" },
            { mx: "80px", fx: "110px", fy: "120px", delay: "160ms" },
          ].map((c, i) => (
            <div
              key={i}
              className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 animate-coin-fly"
              style={{
                ["--mx" as string]: c.mx,
                ["--fx" as string]: c.fx,
                ["--fy" as string]: c.fy,
                animationDelay: c.delay,
              }}
            >
              <div className="coin-trail relative h-full w-full rounded-full bg-gradient-to-br from-[oklch(0.85_0.16_85)] via-[oklch(0.65_0.14_75)] to-[oklch(0.45_0.10_70)] shadow-[0_0_20px_oklch(0.82_0.14_85/0.8)]">
                <div className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-[oklch(0.20_0.05_70)] bg-[oklch(0.20_0.05_70)]" />
              </div>
            </div>
          ))}
        </>
      )}

      <p className="absolute bottom-12 left-1/2 -translate-x-1/2 font-display text-[11px] tracking-[0.4em] text-primary animate-glow">
        THIÊN CƠ HIỂN HIỆN...
      </p>
    </div>
  );
}

const getBienLine = (l: Line): Line => {
  if (!l.changing) return l;
  const isYang = lineToBit(l.value) === "1";
  return { value: isYang ? 6 : 9, changing: false };
};

function DrawingPreview({ lines, drawnCount }: { lines: Line[]; drawnCount: number }) {
  if (drawnCount === 0) return null;
  const hasChanging = lines.slice(0, drawnCount).some((l) => l.changing);

  return (
    <>
      {/* Quẻ gốc ở bên trái */}
      <div className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-20 sm:w-44">
        <div className="flex flex-col-reverse gap-2.5 animate-mystic-rise">
          {lines.map((l, i) => (
            <div key={i} className="relative h-3">
              {i < drawnCount && (
                <div className="flex items-center gap-1 sm:gap-2">
                  <div className="flex-1">
                    <HexLine line={l} animate />
                  </div>
                  {l.changing && (
                    <span className="font-display text-[7px] sm:text-[9px] tracking-widest text-primary animate-glow shrink-0">
                      BIẾN
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Quẻ biến ở bên phải (chỉ hiện khi có ít nhất một hào biến trong các hào đã vẽ) */}
      {hasChanging && (
        <div className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-20 sm:w-44">
          <div className="flex flex-col-reverse gap-2.5 animate-mystic-rise">
            {lines.map((l, i) => {
              const bienLine = getBienLine(l);
              return (
                <div key={i} className="relative h-3">
                  {i < drawnCount && (
                    <div className="flex items-center gap-1 sm:gap-2">
                      <div className="flex-1">
                        <HexLine line={bienLine} animate />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

// ---------- BƯỚC 3-4: hexagram stage with QUẺ BIẾN ----------
function HexagramStage({
  lines,
  activeHao,
  onSelectHao,
  hex,
  backendHexagram,
  onReset,
  canShare,
  onShare,
  canWallpaper,
  onWallpaper,
  captureRef,
  bienLines,
  bienHex,
  bienRevealed,
  hexCracking,
  isMetaCollapsed = false,
  onToggleCollapse,
}: {
  lines: Line[];
  drawnCount: number;
  activeHao: number | null;
  onSelectHao: (i: number) => void;
  hex: Hex | null;
  backendHexagram: HexagramResult | null;
  onReset: () => void;
  canShare?: boolean;
  onShare?: () => void;
  canWallpaper?: boolean;
  onWallpaper?: () => void;
  captureRef?: React.Ref<HTMLDivElement>;
  bienLines: Line[] | null;
  bienHex: Hex | null;
  bienRevealed: boolean;
  hexCracking: boolean;
  isMetaCollapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const hasBien = !!bienLines;
  const main = backendHexagram?.que_chinh;
  const changed = backendHexagram?.que_bien;
  const mainTitle = main?.ten_tuong || main?.ten || hex?.vi;
  const mainSubtitle = [main?.ten, main?.cung_que, main?.ngu_hanh].filter(Boolean).join(" · ");
  const changedTitle = changed?.ten_tuong || changed?.ten || bienHex?.vi;
  const changedSubtitle = [changed?.ten, changed?.cung_que, changed?.ngu_hanh]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="relative flex min-h-full flex-col items-center justify-center px-6 py-6 md:py-8">
      <div
        className="absolute inset-0 -z-10 opacity-40"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, oklch(0.62 0.07 190 / 0.16), transparent 58%)",
        }}
      />

      {/* Toggle collapse button for mobile */}
      {onToggleCollapse && (
        <button
          type="button"
          onClick={onToggleCollapse}
          className="absolute right-3 top-3 z-30 sm:hidden rounded-full border border-gold-soft/50 bg-background/80 px-2.5 py-1 text-[10px] tracking-wider text-primary shadow-sm backdrop-blur"
        >
          {isMetaCollapsed ? "Xem quẻ chi tiết" : "Thu gọn"}
        </button>
      )}

      {/* Vùng chụp ảnh chia sẻ */}
      <div
        ref={captureRef}
        className="flex w-full max-w-2xl flex-col items-center px-4 sm:px-8 py-4 sm:py-6"
      >
        {/* Two-hexagram layout */}
        <div
          className={`flex ${hasBien ? (isMetaCollapsed ? "flex-row gap-6 justify-center items-center" : "flex-row md:grid md:grid-cols-[1fr_auto_1fr]") : "flex-col md:grid md:grid-cols-1"} w-full items-center justify-center gap-3 md:gap-4`}
        >
          {/* QUẺ GỐC */}
          <div data-tour="iching-quegoc" className="relative flex flex-col items-center">
            <span
              className={`font-display text-[9px] md:text-[10px] tracking-[0.3em] md:tracking-[0.5em] text-primary ${isMetaCollapsed ? "hidden sm:inline-block" : "inline-block"}`}
            >
              QUẺ GỐC
            </span>
            <h2
              className={`mt-1 md:mt-2 text-center font-serif-soft text-lg sm:text-xl md:text-2xl italic text-gold-gradient ${isMetaCollapsed ? "text-[15px] sm:text-xl font-semibold" : ""}`}
            >
              {mainTitle}
            </h2>
            <p
              className={`mt-1 text-center text-[10px] sm:text-xs italic text-muted-foreground ${isMetaCollapsed ? "hidden sm:block" : "block"}`}
            >
              {mainSubtitle || hex?.meaning}
            </p>
            {main?.so_que && (
              <p
                className={`mt-1 font-display text-[8px] md:text-[9px] tracking-[0.2em] md:tracking-[0.25em] text-primary/80 ${isMetaCollapsed ? "hidden sm:block" : "block"}`}
              >
                QUẺ SỐ {main.so_que}
              </p>
            )}

            <div
              data-tour="iching-haodong"
              className={`relative mt-3 md:mt-6 flex w-[130px] sm:w-60 flex-col-reverse gap-1.5 sm:gap-3 ${isMetaCollapsed ? "hidden sm:flex" : "flex"}`}
            >
              {lines.map((l, i) => (
                <button
                  key={i}
                  onClick={() => onSelectHao(i)}
                  className="group relative flex items-center gap-1.5 md:gap-2 text-left"
                >
                  <span
                    className={`font-display text-[8px] sm:text-[9px] tracking-widest ${activeHao === i ? "text-primary" : "text-muted-foreground/70"} min-w-[20px] sm:min-w-fit`}
                  >
                    <span className="sm:hidden">{`H${i + 1}`}</span>
                    <span className="hidden sm:inline">{`HÀO ${i + 1}`}</span>
                  </span>
                  <div
                    className={`relative flex-1 ${l.changing && hexCracking ? "hao-spark" : ""}`}
                  >
                    <HexLine line={l} animate={false} active={activeHao === i} />
                  </div>
                  {l.changing && (
                    <span className="iching-accent font-display text-[8px] sm:text-[9px] tracking-widest animate-glow drop-shadow-[0_0_4px_rgba(34,119,128,0.35)]">
                      <span className="sm:hidden">★</span>
                      <span className="hidden sm:inline">BIẾN</span>
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Arrow + biến quẻ */}
          {hasBien && (
            <>
              <div
                className={`flex flex-col items-center justify-center my-1 md:my-0 min-w-8 sm:min-w-[100px] h-6 sm:h-[60px] ${isMetaCollapsed ? "hidden sm:flex" : "flex"}`}
              >
                <div
                  className={`transition-all duration-700 ease-out flex flex-col items-center ${bienRevealed ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-75 -translate-y-2 pointer-events-none"}`}
                >
                  {bienRevealed && (
                    <>
                      <svg
                        width="100"
                        height="40"
                        viewBox="0 0 100 40"
                        className="bien-arrow overflow-visible md:rotate-0 w-8 sm:w-[100px] h-10"
                      >
                        <defs>
                          <linearGradient id="arrG" x1="0" x2="1">
                            <stop offset="0%" stopColor="oklch(0.55 0.08 190)" stopOpacity="0" />
                            <stop
                              offset="35%"
                              stopColor="oklch(0.62 0.09 190)"
                              stopOpacity="0.85"
                            />
                            <stop offset="100%" stopColor="oklch(0.52 0.08 190)" />
                          </linearGradient>
                          <filter id="arrGlow" x="-20%" y="-50%" width="140%" height="200%">
                            <feGaussianBlur stdDeviation="2" result="blur" />
                            <feMerge>
                              <feMergeNode in="blur" />
                              <feMergeNode in="SourceGraphic" />
                            </feMerge>
                          </filter>
                        </defs>
                        <path
                          d="M 2 20 Q 50 14, 92 20"
                          stroke="url(#arrG)"
                          strokeWidth="1.5"
                          fill="none"
                          strokeLinecap="round"
                          filter="url(#arrGlow)"
                          opacity="0.9"
                        />
                        <path
                          d="M 2 20 Q 50 14, 92 20"
                          stroke="url(#arrG)"
                          strokeWidth="3"
                          fill="none"
                          strokeLinecap="round"
                          opacity="0.35"
                        />
                        <path
                          d="M 80 13 L 94 20 L 80 27"
                          stroke="oklch(0.52 0.08 190)"
                          strokeWidth="1.5"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          filter="url(#arrGlow)"
                        />
                        <circle
                          cx="94"
                          cy="20"
                          r="2"
                          fill="oklch(0.52 0.08 190)"
                          filter="url(#arrGlow)"
                        >
                          <animate
                            attributeName="opacity"
                            values="0.6;1;0.6"
                            dur="1.8s"
                            repeatCount="indefinite"
                          />
                        </circle>
                      </svg>
                      <span className="iching-accent mt-2 font-display text-[8px] sm:text-[9px] tracking-[0.2em] sm:tracking-[0.3em]">
                        <span className="sm:hidden">★</span>
                        <span className="hidden sm:inline">BIẾN</span>
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div
                className={`flex flex-col items-center ${bienRevealed ? "bien-emerge" : "opacity-0 pointer-events-none"} ${isMetaCollapsed ? "flex" : "flex"}`}
              >
                <span
                  className={`iching-accent font-display text-[9px] md:text-[10px] tracking-[0.3em] md:tracking-[0.5em] ${isMetaCollapsed ? "hidden sm:inline-block" : "inline-block"}`}
                >
                  QUẺ BIẾN
                </span>
                <h2
                  className={`mt-1 md:mt-2 text-center font-serif-soft text-lg sm:text-xl md:text-2xl italic text-gold-gradient ${isMetaCollapsed ? "text-[15px] sm:text-xl font-semibold" : ""}`}
                >
                  {changedTitle}
                </h2>
                <p
                  className={`mt-1 text-center text-[10px] sm:text-xs italic text-muted-foreground ${isMetaCollapsed ? "hidden sm:block" : "block"}`}
                >
                  {changedSubtitle || bienHex?.meaning}
                </p>
                {changed?.so_que && (
                  <p
                    className={`mt-1 font-display text-[8px] md:text-[9px] tracking-[0.2em] md:tracking-[0.25em] text-primary/80 ${isMetaCollapsed ? "hidden sm:block" : "block"}`}
                  >
                    QUẺ SỐ {changed.so_que}
                  </p>
                )}

                <div
                  className={`mt-3 md:mt-6 flex w-[130px] sm:w-60 flex-col-reverse gap-1.5 sm:gap-3 ${isMetaCollapsed ? "hidden sm:flex" : "flex"}`}
                >
                  {bienLines!.map((l, i) => (
                    <div key={i} className="flex items-center gap-1.5 md:gap-2">
                      <span className="font-display text-[8px] sm:text-[9px] tracking-widest text-muted-foreground/70 min-w-[20px] sm:min-w-fit">
                        <span className="sm:hidden">{`H${i + 1}`}</span>
                        <span className="hidden sm:inline">{`HÀO ${i + 1}`}</span>
                      </span>
                      <div
                        className={`relative flex-1 ${l.changing && hexCracking ? "hao-spark" : ""}`}
                      >
                        <HexLine line={l} animate={false} active={l.changing} />
                      </div>
                      {l.changing && (
                        <span className="iching-accent font-display text-[8px] sm:text-[9px] tracking-widest animate-glow drop-shadow-[0_0_4px_rgba(34,119,128,0.35)]">
                          <span className="sm:hidden">★</span>
                          <span className="hidden sm:inline">BIẾN</span>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <p
          className={`mt-4 sm:mt-8 max-w-md text-center text-[11px] italic leading-relaxed text-muted-foreground/80 ${isMetaCollapsed ? "hidden sm:block" : "block"}`}
        >
          {hasBien
            ? "Quẻ Gốc cho hiện tại, Quẻ Biến chỉ tương lai khi hào động chuyển hoá."
            : "Quẻ tĩnh — không có hào biến. Tâm bạn đang ở thế quân bình."}
        </p>
      </div>

      {/* Chỉ desktop: dưới `lg` mọi nút này đã có trong dải công cụ
          của bảng luận, giữ cả hai là hai hàng nút trùng nhau. */}
      <div
        data-share-exclude
        className="mt-4 hidden flex-wrap items-center justify-center gap-2 sm:mt-6 lg:flex"
      >
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 rounded-full border border-gold-soft/80 bg-card/70 px-5 py-2 text-xs font-medium text-foreground/72 shadow-sm transition hover:border-primary/60 hover:text-primary dark:bg-background/40 dark:text-muted-foreground"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Lập đàn lại
        </button>
        {canShare && onShare && (
          <button
            onClick={onShare}
            className="inline-flex items-center gap-2 rounded-full border iching-accent-border bg-[oklch(0.94_0.03_190/0.55)] px-5 py-2 text-xs font-semibold iching-accent shadow-sm transition hover:bg-[oklch(0.9_0.04_190/0.7)] dark:bg-[oklch(0.2_0.055_190/0.2)]"
          >
            <Share2 className="h-3.5 w-3.5" /> Chia sẻ quẻ
          </button>
        )}
        {canWallpaper && onWallpaper && (
          <button
            onClick={onWallpaper}
            className="inline-flex items-center gap-2 rounded-full border border-gold-soft/80 bg-card/70 px-5 py-2 text-xs font-semibold text-foreground/80 shadow-sm transition hover:border-primary/60 hover:text-primary dark:bg-background/40"
          >
            <Wallpaper className="h-3.5 w-3.5" /> Tạo ảnh nền
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- AI mock content generators ----------
function _primaryReading(question: string, lines: Line[]): string {
  const changing = lines.map((l, i) => (l.changing ? i + 1 : null)).filter(Boolean);
  return [
    `## Lời quẻ chính\n`,
    `Câu hỏi của bạn — *"${question}"* — chạm vào một quẻ mang khí chất **chuyển dịch trong tĩnh tại**. Thiên tượng cho thấy nội tâm đã chín muồi nhưng ngoại cảnh chưa hoàn toàn thuận. Đây là **thời điểm vàng để chuẩn bị**, chưa phải để bứt phá.\n\n`,
    `### Tượng quẻ\n`,
    `Sáu vạch hợp thành tượng *"trên cương dưới nhu, trong sáng ngoài tĩnh"*. Vận trời ủng hộ người **biết chờ đợi đúng lúc** — không phải kẻ liều lĩnh.\n\n`,
    `### Hào từ trọng yếu\n`,
    `- **Sơ hào (dưới cùng)**: Khởi sự cẩn trọng, đừng vội phô trương. Như mầm non vừa ló — hãy tưới tẩm bằng kiên nhẫn.\n`,
    `- **Tam hào**: Sẽ gặp thử thách giữa đường, nhưng đây là **lửa tôi luyện vàng ròng**, không phải tai họa.\n`,
    `- **Thượng hào (trên cùng)**: Khi đến đỉnh, biết dừng lại đúng lúc — phúc khí mới bền lâu.\n\n`,
    changing.length
      ? `### Hào động\nQuẻ này có **Hào ${changing.join(", ")}** đang biến — đây chính là điểm then chốt cần soi xét. Hãy bấm vào hào sáng để thỉnh giáo sâu hơn.\n\n`
      : `### Hào động\nQuẻ tĩnh — không có hào biến rõ rệt. Tâm bạn đang ở thế quân bình, hãy giữ vững.\n\n`,
    `### Lời khuyên\n`,
    `Trong **49 ngày tới**, giữ tâm an định, tránh quyết định lớn vào giờ Tý và giờ Ngọ. Hướng cát: **Đông Nam**. Người trợ duyên mang mệnh **Mộc** hoặc **Thủy**.\n\n`,
    `> *"Cùng tắc biến, biến tắc thông, thông tắc cửu."* — Khi đã bí thì phải biến, biết biến thì sẽ thông, đã thông thì bền lâu.`,
  ].join("");
}

function _followUpReading(question: string, _lines: Line[], activeHao: number | null): string {
  const haoLabel = activeHao !== null ? `Hào ${activeHao + 1}` : "Hào động";
  return [
    `### Thỉnh giáo về ${haoLabel}\n`,
    `Câu hỏi *"${question}"* được chiếu rọi qua **${haoLabel}** — vạch đang dao động trong quẻ chủ.\n\n`,
    `Hào này ứng vào việc bạn vừa hỏi như sau: thoạt nhìn có vẻ **trắc trở**, nhưng kỳ thực là *cánh cửa hẹp dẫn đến lối lớn*. Cổ nhân gọi đây là **"khốn nhi hanh"** — gặp khốn rồi mới hanh thông.\n\n`,
    `**Ba điều cần lưu ý:**\n`,
    `1. Đừng tranh cãi với người mệnh **Kim** trong 21 ngày tới.\n`,
    `2. Một cơ hội nhỏ sẽ đến từ **người quen cũ** — đừng bỏ qua.\n`,
    `3. Giữ chữ *Tín* — đó là vốn liếng lớn nhất của bạn lúc này.\n\n`,
    `*Quẻ đã dứt lời ở đoạn này — nếu còn băn khoăn, hãy thỉnh giáo thêm.*`,
  ].join("");
}
